/**
 * CanvasBoard.tsx
 * 
 * 职责：
 * 1. 提供绘图应用的核心画布组件
 * 2. 处理所有与画布相关的用户交互，如鼠标事件和键盘事件
 * 3. 管理画布的渲染循环和状态更新
 * 4. 协调DrawingService、TextService、SelectionService、GridService和ViewportService服务之间的交互
 * 5. 处理不同工具模式下(绘图、选择、文本、形状)的事件逻辑
 * 6. 实现画布的缩放、平移和视口管理
 * 7. 提供上下文菜单和键盘快捷键功能
 * 8. 管理画布大小随窗口调整的响应式行为
 * 9. 处理绘图元素的碰撞检测和选择逻辑
 * 10. 支持元素组合功能
 */

import React, { useRef, useEffect, useState, useCallback, useLayoutEffect } from 'react';
import { CanvasMode, Point, ContextMenuState, LineData, TextData, Bounds } from '../../types';
import { DrawingService } from '../services/DrawingService';
import { TextService } from '../services/TextService';
import { SelectionService } from '../services/SelectionService';
import { GridService } from '../services/GridService';
import { ViewportService } from '../services/ViewportService';
import { CardService } from '../services/CardService';
import { GroupService } from '../services/GroupService';
import { ImageService } from '../services/ImageService';
import { ResizeService } from '../services/ResizeService';

interface CanvasBoardProps {
  canvasMode: CanvasMode;
  drawingService: DrawingService;
  textService: TextService;
  selectionService: SelectionService;
  gridService: GridService;
  viewportService: ViewportService;
  cardService: CardService;
  groupService: GroupService;
  imageService: ImageService;
  resizeService: ResizeService;
  setContextMenuState: (state: ContextMenuState) => void;
  copiedLines: LineData[];
  setCopiedLines: (lines: LineData[]) => void;
  completeLineSelection: (addToExisting: boolean) => void;
  moveSelectedContent: (point: Point) => void;
  handleStopMoving: () => void;
}

const CanvasBoard: React.FC<CanvasBoardProps> = ({
  canvasMode,
  drawingService,
  textService,
  selectionService,
  gridService,
  viewportService,
  cardService,
  groupService,
  imageService,
  resizeService,
  setContextMenuState,
  copiedLines,
  setCopiedLines,
  completeLineSelection,
  moveSelectedContent,
  handleStopMoving
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const [isSpacePressed, setIsSpacePressed] = useState<boolean>(false);
  const [isRightDragging, setIsRightDragging] = useState<boolean>(false);
  const [mouseDownPoint, setMouseDownPoint] = useState<Point | null>(null);
  const [clientMouseDownPoint, setClientMouseDownPoint] = useState<{x: number, y: number, time: number} | null>(null);
  const textAddTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isZoomLocked, setIsZoomLocked] = useState<boolean>(false);
  const [isFixed, setIsFixed] = useState<boolean>(false);
  const [showExtendedPanel, setShowExtendedPanel] = useState<boolean>(false);
  const [isZoomButtonHovered, setIsZoomButtonHovered] = useState<boolean>(false);
  const [isFixButtonHovered, setIsFixButtonHovered] = useState<boolean>(false);
  const [hoveredTextIndex, setHoveredTextIndex] = useState<number>(-1);
  const [tooltipPosition, setTooltipPosition] = useState<{x: number, y: number}>({x: 0, y: 0});
  const wheelEndTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [isContentOutOfView, setIsContentOutOfView] = useState<boolean>(false);
  const [isNearZoomLimit, setIsNearZoomLimit] = useState<boolean>(false);
  // 添加状态消息提示的状态
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [showStatusMessage, setShowStatusMessage] = useState<boolean>(false);
  const statusMessageTimerRef = useRef<NodeJS.Timeout | null>(null);
  // 添加一个状态变量，用于跟踪是否正在进行控制点拖动
  const [isDraggingControlPoint, setIsDraggingControlPoint] = useState<boolean>(false);
  const [activeControlPointPosition, setActiveControlPointPosition] = useState<'TOP_LEFT' | 'TOP_RIGHT' | 'BOTTOM_LEFT' | 'BOTTOM_RIGHT' | 'TOP_CENTER' | 'RIGHT_CENTER' | 'BOTTOM_CENTER' | 'LEFT_CENTER' | null>(null);
  
  // 根据当前模式更新鼠标样式
  useEffect(() => {
    if (!canvasRef.current) return;
    
    // 如果画布被固定，强制使用默认光标
    if (isFixed) {
      canvasRef.current.style.cursor = 'default';
      return;
    }
    
    // 如果正在拖动视口，使用抓取光标
    if (isSpacePressed || isRightDragging) {
      canvasRef.current.style.cursor = viewportService.isDraggingViewport() ? 'grabbing' : 'grab';
      return;
    }
    
    // 根据当前模式设置光标
    switch (canvasMode) {
      case CanvasMode.TEXT:
        canvasRef.current.style.cursor = 'text';
        break;
      case CanvasMode.SELECT:
        canvasRef.current.style.cursor = 'default';
        break;
      case CanvasMode.DRAW:
        canvasRef.current.style.cursor = 'crosshair';
        break;
      case CanvasMode.SHAPE:
        canvasRef.current.style.cursor = 'crosshair';
        break;
      default:
        canvasRef.current.style.cursor = 'default';
    }
  }, [canvasMode, isSpacePressed, isRightDragging, isFixed, viewportService]);

  // 确保在工具模式切换时保持缩放锁定状态
  useEffect(() => {
    // 每当工具模式切换时，重新应用缩放锁定状态
    if (isZoomLocked || isFixed) {
      viewportService.setZoomDisabled(true);
    } else {
      viewportService.setZoomDisabled(false);
    }
  }, [canvasMode, isZoomLocked, isFixed, viewportService]);

  // 渲染函数 - 绘制所有画布元素
  const render = useCallback(() => {
    if (!canvasRef.current) return;
    
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    
    // 清空画布
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    
    // 设置合成模式，确保透明度保持
    ctx.globalCompositeOperation = 'source-over';
    
    // 应用视口变换
    ctx.save();
    viewportService.applyTransform(ctx);
    
    // 获取画布实际尺寸
    const { width, height } = viewportService.getCanvasSize();
    
    // 确保所有服务的上下文是最新的
    drawingService.setContext(ctx);
    textService.setContext(ctx);
    selectionService.setContext(ctx);
    gridService.setContext(ctx);
    cardService.setContext(ctx);
    imageService.setContext(ctx);
    
    // 先绘制网格
    gridService.drawGrid(width, height);
    
    // 绘制所有元素，顺序决定了渲染层级
    imageService.drawAllImages();     // 绘制图片
    drawingService.drawAllElements(); // 绘制线条和形状
    cardService.drawAllCards();       // 绘制卡片
    textService.drawAllTexts();       // 绘制文本
    
    // 绘制选择框和选中内容
    if (canvasMode === CanvasMode.SELECT) {
      const lines = drawingService.getLines();
      const texts = textService.getTexts();
      const cards = cardService.getCards();
      const images = imageService.getImages();
      selectionService.drawSelectedContentBounds(lines, texts, cards, images);
    }
    
    // 绘制选择预览（当用户拖动选择框时）
    if (canvasMode === CanvasMode.SELECT && selectionService.isSelecting()) {
      selectionService.previewSelection(drawingService.getLines(), textService.getTexts(), cardService.getCards(), imageService.getImages());
    }
    
    ctx.restore();
  }, [
    drawingService, 
    textService,
    cardService,
    imageService,
    selectionService, 
    gridService, 
    viewportService, 
    canvasMode
  ]);

  // 将客户端坐标转换为画布坐标
  const getCanvasPoint = useCallback((clientX: number, clientY: number): Point => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    
    return viewportService.clientToCanvas(x, y);
  }, [viewportService]);

  // 初始化画布
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    // 设置上下文
    drawingService.setContext(ctx);
    textService.setContext(ctx);
    selectionService.setContext(ctx);
    gridService.setContext(ctx);
    cardService.setContext(ctx);
    imageService.setContext(ctx);
    
    // 设置视口服务
    textService.setViewportService(viewportService);
    cardService.setViewportService(viewportService);
    imageService.setViewportService(viewportService);

    // 监听文本渲染需求事件
    const handleRenderRequired = () => {
      render();
    };
    
    canvas.addEventListener('render-required', handleRenderRequired);

    // 设置画布大小
    const resizeCanvas = () => {
      if (!containerRef.current || !canvas) return;
      
      const { width, height } = containerRef.current.getBoundingClientRect();
      
      // 设置DPI缩放
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      
      // 设置CSS大小
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      
      // 更新视口服务的画布尺寸
      viewportService.resizeCanvas(canvas.width, canvas.height);
      
      // 重新设置上下文（确保在大小调整后上下文仍然有效）
      const newCtx = canvas.getContext('2d', { alpha: true });
      if (newCtx) {
        drawingService.setContext(newCtx);
        textService.setContext(newCtx);
        selectionService.setContext(newCtx);
        gridService.setContext(newCtx);
        cardService.setContext(newCtx);
        imageService.setContext(newCtx);
      }
      
      // 重新渲染
      render();
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // 强制再次渲染，确保网格显示
    setTimeout(() => {
      render();
    }, 100);
    
    // 添加防闪烁补丁，在TextService处理文本编辑时保持稳定渲染
    // 我们不再使用DOM文本编辑器，只需要监听TextService的事件
    let renderAnimationFrame: number | null = null;
    
    // 监听文本编辑状态变化
    const handleTextEditStart = () => {
      // 文本编辑开始时，使用更高效的渲染方式
      if (!renderAnimationFrame) {
        const lastRenderTime = { value: 0 };
        
        const smoothRender = (timestamp: number) => {
          // 控制渲染频率，每33ms渲染一次（约30fps），避免过度渲染
          if (timestamp - lastRenderTime.value >= 33) {
            render();
            lastRenderTime.value = timestamp;
          }
          renderAnimationFrame = requestAnimationFrame(smoothRender);
        };
        renderAnimationFrame = requestAnimationFrame(smoothRender);
      }
    };

    const handleTextEditComplete = () => {
      // 文本编辑完成时，停止高频渲染
      if (renderAnimationFrame) {
        cancelAnimationFrame(renderAnimationFrame);
        renderAnimationFrame = null;
        
        // 编辑完成后再渲染一次，确保最终状态正确显示
        render();
      }
    };

    // 监听文本编辑事件
    document.addEventListener('text-edit-start', handleTextEditStart);
    document.addEventListener('text-edit-complete', handleTextEditComplete);
    
    // 组件卸载时的清理
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      canvas.removeEventListener('render-required', handleRenderRequired);
      
      if (renderAnimationFrame) {
        cancelAnimationFrame(renderAnimationFrame);
      }
      document.removeEventListener('text-edit-start', handleTextEditStart);
      document.removeEventListener('text-edit-complete', handleTextEditComplete);
    };
  }, [drawingService, textService, selectionService, gridService, viewportService, cardService, imageService, render]);

  // 设置ViewportService的渲染回调
  useEffect(() => {
    viewportService.setRenderCallback(render);
    
    return () => {
      viewportService.setRenderCallback(null);
    };
  }, [viewportService, render]);

  // 键盘事件处理
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // 如果用户正在编辑文本，不处理键盘事件
      if (document.querySelector('textarea[data-text-editor="true"]')) {
        return;
      }
      
      // 如果用户正在编辑卡片，不处理键盘事件
      if (document.querySelector('textarea[data-card-editor="true"]')) {
        return;
      }
      
      // 如果用户正在编辑注释，不处理键盘事件
      if (document.querySelector('textarea[data-annotation-editor="true"]')) {
        return;
      }
      
      // 处理ESC键 - 取消当前创建操作
      if (e.key === 'Escape') {
        // 正在绘制线条时，取消绘制
        if (canvasMode === CanvasMode.DRAW) {
          // 使用DrawingService的cancelDrawing方法
          drawingService.cancelDrawing();
          setIsDrawing(false);
          render();
          return;
        }
        
        // 正在绘制形状时，取消形状绘制
        if (canvasMode === CanvasMode.SHAPE) {
          // 取消形状绘制
          drawingService.cancelShapeCreation();
          setIsDrawing(false);
          render();
          return;
        }
        
        // 如果在其他工具模式下，按ESC也可以触发工具操作完成事件
        document.dispatchEvent(new CustomEvent('tool-operation-complete'));
      }
      
      // 处理删除键和退格键
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // 如果当前模式是选择模式且有选中的内容
        if (canvasMode === CanvasMode.SELECT) {
          // 删除选中内容
          selectionService.deleteSelectedContent(drawingService, textService, cardService, imageService);
          render();
        }
      }
      
      // 空格键：进入平移模式
      if (e.key === ' ' && !isSpacePressed && !isFixed) {
        setIsSpacePressed(true);
      }
      
      // Ctrl+C：复制选中内容
      if (e.ctrlKey && e.key === 'c' && selectionService.getSelectedIndices().length > 0) {
        const selectedLines = selectionService.getSelectedIndices().map(
          index => drawingService.getLines()[index]
        );
        setCopiedLines([...selectedLines]);
      }
      
      // Ctrl+V：粘贴内容
      if (e.ctrlKey && e.key === 'v' && copiedLines.length > 0) {
        const newLines = copiedLines.map(line => {
          const newPath = new Path2D();
          if (line.points.length > 0) {
            newPath.moveTo(line.points[0].x, line.points[0].y);
            for (let i = 1; i < line.points.length; i++) {
              newPath.lineTo(line.points[i].x, line.points[i].y);
            }
          }
          
          return {
            ...line,
            path: newPath,
            points: [...line.points.map(point => ({ ...point }))]
          };
        });
        
        const currentLines = drawingService.getLines();
        drawingService.setLines([...currentLines, ...newLines]);
        
        const newIndices = Array.from(
          { length: newLines.length },
          (_, i) => currentLines.length + i
        );
        selectionService.setSelectedIndices(newIndices);
        
        render();
      }
    };
    
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === ' ') {
        setIsSpacePressed(false);
        if (viewportService.isDraggingViewport()) {
          viewportService.stopDrag();
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [
    drawingService, 
    selectionService, 
    viewportService, 
    render, 
    isSpacePressed, 
    copiedLines, 
    setCopiedLines,
    isFixed
  ]);

  // 鼠标滚轮事件：根据当前状态执行缩放或平移
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      
      if (canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        // 检查是否在编辑文本或卡片
        const isEditingText = textService.getTexts().some((text: TextData) => text.isEditing);
        const isEditingCard = document.querySelector('textarea[data-card-editor="true"]') !== null;
        
        // 如果画布被固定，禁止任何移动
        if (isFixed) {
          return;
        }
        
        // 滚轮行为取决于用户设置的画布模式
        const shouldPerformPan = isZoomLocked;
        
        // 如果应该执行平移操作（基于用户的缩放锁定设置）
        if (shouldPerformPan) {
          // 获取滚动量并计算平移距离
          const deltaY = e.deltaY;
          
          // 改进的滚轮类型检测
          // 更精确地检测是否为触控板或精确滚动设备
          const isSmoothScroll = Math.abs(deltaY) < 40 && Math.abs(deltaY) > 0.1;
          
          // 动态调整平移系数，使移动更加平滑
          // 对于小的deltaY值使用较大的系数，对于大的deltaY值使用较小的系数
          // 使用更平滑的动态因子计算
          const dynamicFactor = Math.max(1.5, Math.min(4.0, 8.0 / Math.pow(Math.abs(deltaY), 0.4)));
          const basePanFactor = isSmoothScroll ? dynamicFactor : dynamicFactor * 1.2;
          
          // 获取当前缩放比例，用于调整移动距离
          const scale = viewportService.getScale();
          
          // 计算移动距离，确保在不同缩放比例下有一致的体验
          // 对于快速滚动，限制最大移动距离，防止画布跳跃
          const maxDistance = isSmoothScroll ? 40 : 70; // 减小最大移动距离，使滚动更平滑
          let moveDistance = (deltaY > 0 ? -1 : 1) * Math.min(Math.abs(deltaY) * basePanFactor, maxDistance) / scale;
          
          // 应用更平滑的非线性映射，使小的移动更精确，大的移动更平滑
          moveDistance = Math.sign(moveDistance) * Math.pow(Math.abs(moveDistance), 0.85);
          
          // 如果按住Ctrl键，减小移动距离实现微调
          if (e.ctrlKey) {
            moveDistance *= 0.25; // 将移动距离缩小到原来的25%
          }
          
          // 根据是否按下Shift键决定水平还是垂直平移
          if (e.shiftKey) {
            // 水平平移
            const currentOffsetX = viewportService.getOffsetX();
            const targetOffsetX = currentOffsetX + moveDistance;
            viewportService.startSmoothScroll(targetOffsetX, viewportService.getOffsetY());
          } else {
            // 垂直平移
            const currentOffsetY = viewportService.getOffsetY();
            const targetOffsetY = currentOffsetY + moveDistance;
            viewportService.startSmoothScroll(viewportService.getOffsetX(), targetOffsetY);
          }
          
          // 注意：不再需要在这里更新编辑元素位置
          // 现在通过viewport-scroll-update事件在每一帧动画中更新
          
          // 改进的防抖处理：添加一个延迟，在滚轮操作结束后触发保存事件
          // 同时确保在连续滚动时不会频繁触发保存
          if (wheelEndTimerRef.current) {
            clearTimeout(wheelEndTimerRef.current);
          }
          
          wheelEndTimerRef.current = setTimeout(() => {
            // 触发工具操作完成事件，自动保存当前内容状态
            document.dispatchEvent(new CustomEvent('tool-operation-complete'));
            // 确保在保存后重置定时器引用
            wheelEndTimerRef.current = null;
            // 在滚动结束后，确保视图稳定
            render();
          }, 300); // 增加延迟时间，减少保存频率
          
          return;
        }
        
        // 如果未锁定缩放，执行正常缩放操作
        const direction = e.deltaY < 0 ? 1 : -1;
        const isPreciseZoom = e.ctrlKey;
        
        viewportService.zoom(direction, x, y, isPreciseZoom);
      }
    };
    
    canvasRef.current?.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      canvasRef.current?.removeEventListener('wheel', handleWheel);
      
      // 清理滚轮防抖定时器
      if (wheelEndTimerRef.current) {
        clearTimeout(wheelEndTimerRef.current);
        wheelEndTimerRef.current = null;
      }
    };
  }, [canvasMode, isFixed, isZoomLocked, render, textService, drawingService, viewportService]);

  // -------- 辅助函数 -------- //
  
  // 查找点击位置所在的线条
  const findClickedLine = (point: Point, lines: LineData[]): number => {
    // 使用DrawingService的hitTest方法来检测点击的线条或形状
    return drawingService.hitTest(point);
  };

  // 查找点击位置所在的文本
  const findClickedText = (point: Point): number => {
    // 使用TextService的hitTest方法来检测点击的文本
    return textService.hitTest(point);
  };

  // 查找点击位置所在的卡片
  const findClickedCard = (point: Point): number => {
    return cardService.hitTest(point);
  };
  
  // 添加查找点击图片的函数
  const findClickedImage = (point: Point): number => {
    return imageService.findClickedImageIndex(point);
  };
  
  // 计算点到线段的最短距离的函数
  const distanceToLineSegment = (point: Point, lineStart: Point, lineEnd: Point): number => {
    const dx = lineEnd.x - lineStart.x;
    const dy = lineEnd.y - lineStart.y;
    
    // 线段长度的平方
    const lengthSquared = dx * dx + dy * dy;
    
    // 如果线段实际上是一个点
    if (lengthSquared === 0) {
      return Math.sqrt(
        Math.pow(point.x - lineStart.x, 2) + 
        Math.pow(point.y - lineStart.y, 2)
      );
    }
    
    // 计算投影点的参数 t (0 <= t <= 1)
    const t = Math.max(0, Math.min(1, 
      ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / lengthSquared
    ));
    
    // 计算投影点坐标
    const projectionX = lineStart.x + t * dx;
    const projectionY = lineStart.y + t * dy;
    
    // 计算点到投影点的距离
    return Math.sqrt(
      Math.pow(point.x - projectionX, 2) + 
      Math.pow(point.y - projectionY, 2)
    );
  };
  
  // 阻止默认上下文菜单并显示自定义菜单
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    // 阻止默认右键菜单
    e.preventDefault();
    
    // 如果已经有注释编辑框打开，不处理右键点击
    if (document.querySelector('.annotation-input-container')) {
      return;
    }
    
    // 检查是否发生了拖拽（通过比较屏幕坐标）
    if (clientMouseDownPoint && (
      Math.abs(e.clientX - clientMouseDownPoint.x) > 0.001 || 
      Math.abs(e.clientY - clientMouseDownPoint.y) > 0.001
    )) {
      // 如果发生了拖拽，不显示上下文菜单
      return;
    }
    
    // 获取点击位置的画布坐标
    const point = getCanvasPoint(e.clientX, e.clientY);
    
    // 获取当前选中状态
    const selectedIndices = selectionService.getSelectedIndices();
    const selectedTextIndices = selectionService.getSelectedTextIndices();
    const selectedCardIndices = selectionService.getSelectedCardIndices();
    const selectedImageIndices = selectionService.getSelectedImageIndices();
    const selectedGroupId = selectionService.getSelectedGroupId();
    
    // 如果有选中的内容，检查点击是否在选中内容的边界框内
    if (selectedIndices.length > 0 || selectedTextIndices.length > 0 || selectedCardIndices.length > 0 || selectedImageIndices.length > 0) {
      const bounds = selectionService.getSelectedContentBounds(
        drawingService.getLines(), 
        textService.getTexts(), 
        cardService.getCards(),
        imageService.getImages()
      );
      
      // 增加边距，使检测区域略大于实际边界
      const padding = 5;
      const extendedBounds = {
        minX: bounds.minX - padding,
        minY: bounds.minY - padding,
        maxX: bounds.maxX + padding,
        maxY: bounds.maxY + padding
      };
      
      // 检查点击是否在边界框内
      if (
        point.x >= extendedBounds.minX && point.x <= extendedBounds.maxX &&
        point.y >= extendedBounds.minY && point.y <= extendedBounds.maxY
      ) {
        // 首先检查是否点击在文本框内
        const clickedTextIndex = findClickedText(point);
        if (clickedTextIndex !== -1 && selectedTextIndices.includes(clickedTextIndex)) {
          // 检查文本是否属于组
          if (selectedGroupId) {
            // 如果文本属于组，显示组上下文菜单
            setContextMenuState({
              visible: true,
              x: e.clientX,
              y: e.clientY,
              type: 'group',
              groupId: selectedGroupId
            });
          } else {
            // 否则显示文本菜单
            setContextMenuState({
              visible: true,
              x: e.clientX,
              y: e.clientY,
              type: 'text',
              textIndex: clickedTextIndex
            });
          }
          return;
        }
        
        // 检查是否点击在图片上
        const clickedImageIndex = findClickedImage(point);
        if (clickedImageIndex !== -1 && selectedImageIndices.includes(clickedImageIndex)) {
          // 检查图片是否属于组
          if (selectedGroupId) {
            // 如果图片属于组，显示组上下文菜单
            setContextMenuState({
              visible: true,
              x: e.clientX,
              y: e.clientY,
              type: 'group',
              groupId: selectedGroupId
            });
          } else {
            // 否则显示图片菜单
            setContextMenuState({
              visible: true,
              x: e.clientX,
              y: e.clientY,
              type: 'image',
              imageIndex: clickedImageIndex
            });
          }
          return;
        }
        
        // 对于非文本点击，确定要显示的菜单类型
        let menuType: 'line' | 'text' | 'card' | 'group' | 'empty' | 'image';
        let menuIndex = -1;
        
        if (selectedGroupId) {
          // 如果有选中的组合，显示组合菜单
          menuType = 'group';
        } else if (selectedCardIndices.length > 0) {
          // 有选中的卡片，显示卡片菜单
          menuType = 'card';
          menuIndex = selectedCardIndices[0];
        } else if (selectedImageIndices.length > 0) {
          // 有选中的图片，显示图片菜单
          menuType = 'image';
          menuIndex = selectedImageIndices[0];
        } else if (selectedIndices.length > 0) {
          // 有选中的线条，显示线条菜单
          menuType = 'line';
          menuIndex = selectedIndices[0];
        } else {
          // 默认为线条类型
          menuType = 'line';
        }
        
        // 显示对应类型的上下文菜单
        setContextMenuState({
          visible: true,
          x: e.clientX,
          y: e.clientY,
          type: menuType,
          ...(menuType === 'line' ? { lineIndex: menuIndex } : {}),
          ...(menuType === 'card' ? { cardIndex: menuIndex } : {}),
          ...(menuType === 'image' ? { imageIndex: menuIndex } : {}),
          ...(menuType === 'group' || selectedGroupId ? { groupId: selectedGroupId } : {})
        });
        return;
      }
    }
    
    // 检查是否点击在文本上
    const textIndex = findClickedText(point);
    if (textIndex !== -1) {
      // 先清除当前选择
      selectionService.clearSelection();
      
      // 检查文本是否属于某个组
      const groupId = groupService.isElementInAnyGroup('text', textIndex);
      if (groupId) {
        // 如果文本属于组，选择整个组
        selectionService.selectGroup(groupId);
        
        // 显示组上下文菜单
        setContextMenuState({
          visible: true,
          x: e.clientX,
          y: e.clientY,
          type: 'group',
          groupId: groupId
        });
      } else {
        // 选中该文本
        selectionService.setSelectedTextIndices([textIndex]);
        
        // 点击在文本上，显示文本上下文菜单
        setContextMenuState({
          visible: true,
          x: e.clientX,
          y: e.clientY,
          type: 'text',
          textIndex: textIndex
        });
      }
      
      // 触发重绘以显示选择框
      render();
      return;
    }
    
    // 检查是否点击在线条上
    const lineIndex = findClickedLine(point, drawingService.getLines());
    if (lineIndex !== -1) {
      // 先清除当前选择
      selectionService.clearSelection();
      
      // 检查线条是否属于某个组
      const groupId = groupService.isElementInAnyGroup('line', lineIndex);
      if (groupId) {
        // 如果线条属于组，选择整个组
        selectionService.selectGroup(groupId);
        
        // 显示组上下文菜单
        setContextMenuState({
          visible: true,
          x: e.clientX,
          y: e.clientY,
          type: 'group',
          groupId: groupId
        });
      } else {
        // 使用selectElement方法处理选择，添加到现有选择
        selectionService.selectElement('line', lineIndex, true);
        
        // 显示线条上下文菜单
        setContextMenuState({
          visible: true,
          x: e.clientX,
          y: e.clientY,
          type: 'line',
          lineIndex: lineIndex
        });
      }
      
      // 触发重绘以显示选择框
      render();
      return;
    }
    
    // 检查是否点击在卡片上
    const cardIndex = findClickedCard(point);
    if (cardIndex !== -1) {
      // 先清除当前选择
      selectionService.clearSelection();
      
      // 检查卡片是否属于某个组
      const groupId = groupService.isElementInAnyGroup('card', cardIndex);
      if (groupId) {
        // 如果卡片属于组，选择整个组
        selectionService.selectGroup(groupId);
        
        // 显示组上下文菜单
        setContextMenuState({
          visible: true,
          x: e.clientX,
          y: e.clientY,
          type: 'group',
          groupId: groupId
        });
      } else {
        // 选中该卡片
        selectionService.setSelectedCardIndices([cardIndex]);
        
        // 点击在卡片上，显示卡片上下文菜单
        setContextMenuState({
          visible: true,
          x: e.clientX,
          y: e.clientY,
          type: 'card',
          cardIndex: cardIndex
        });
      }
      
      // 触发重绘以显示选择框
      render();
      return;
    }
    
    // 检查点击是否在图片上
    const imageIndex = findClickedImage(point);
    if (imageIndex !== -1) {
      // 获取当前选中的图片
      const selectedImageIndices = selectionService.getSelectedImageIndices();
      
      // 如果点击的图片已经在选中状态中
      if (selectedImageIndices.includes(imageIndex)) {
        // 检查图片是否属于组
        const groupId = selectionService.getSelectedGroupId();
        if (groupId) {
          // 如果图片属于组，显示组上下文菜单
          setContextMenuState({
            visible: true,
            x: e.clientX,
            y: e.clientY,
            type: 'group',
            groupId: groupId
          });
        } else {
          // 否则显示图片菜单
          setContextMenuState({
            visible: true,
            x: e.clientX,
            y: e.clientY,
            type: 'image',
            imageIndex: imageIndex
          });
        }
        return;
      }
      
      // 先清除当前选择
      selectionService.clearSelection();
      
      // 检查图片是否属于某个组
      const groupId = groupService.isElementInAnyGroup('image', imageIndex);
      if (groupId) {
        // 如果图片属于组，选择整个组
        selectionService.selectGroup(groupId);
        
        // 显示组上下文菜单
        setContextMenuState({
          visible: true,
          x: e.clientX,
          y: e.clientY,
          type: 'group',
          groupId: groupId
        });
      } else {
        // 选中该图片
        selectionService.setSelectedImageIndices([imageIndex]);
        
        // 点击在图片上，显示图片上下文菜单
        setContextMenuState({
          visible: true,
          x: e.clientX,
          y: e.clientY,
          type: 'image',
          imageIndex: imageIndex
        });
      }
      
      // 触发重绘以显示选择框
      render();
      return;
    }
    
    // 如果画布被固定，则不启动拖动模式
    if (isFixed) return;
    
    // 空白处右键点击：显示空白区域菜单
    setContextMenuState({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      type: 'empty'
    });
    
    // 注意：右键拖动由mousedown事件处理，不在这里处理
  }, [
    drawingService, 
    textService, 
    selectionService, 
    groupService,
    cardService,
    setContextMenuState, 
    findClickedText, 
    findClickedLine, 
    findClickedCard, 
    getCanvasPoint, 
    render, 
    isFixed, 
    clientMouseDownPoint,
    findClickedImage
  ]);

  // 鼠标按下事件
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!canvasRef.current) return;
    
    // 获取画布坐标点
    const point = getCanvasPoint(e.clientX, e.clientY);
    
    // 如果当前有文本正在编辑，则阻止切换模式
    if (textService.getTexts().some((text: TextData) => text.isEditing)) {
      const clickedTextIndex = findClickedText(point);
      if (clickedTextIndex !== -1) {
        const texts = textService.getTexts();
        if (texts[clickedTextIndex].isEditing) {
          // 如果点击的是正在编辑的文本，则更新光标位置
          
          e.stopPropagation();
          e.preventDefault();
        }
      }
      return;
    }
    
    e.preventDefault();
    
    // 更新鼠标按下位置（同时记录画布坐标、客户端坐标和时间戳）
    setMouseDownPoint(point);
    setClientMouseDownPoint({
      x: e.clientX, 
      y: e.clientY,
      time: Date.now() // 添加时间戳记录鼠标按下的时间
    });
    
    // 在固定模式下，右键点击不应触发任何操作
    if (isFixed && e.button === 2) {
      return;
    }
    
    // 处理右键点击 - 只在未固定状态下允许右键拖动画布
    if (e.button === 2 && !isFixed) {
      // 在mousedown阶段就启动拖拽，确保立即跟随鼠标移动
      viewportService.startDrag(e.clientX, e.clientY);
      setIsRightDragging(true);
      return;
    }
    
    // 空格键按下时处理平移（固定模式下不处理平移）
    if (isSpacePressed && !isFixed) {
      viewportService.startDrag(e.clientX, e.clientY);
      return;
    }
    
    // 首先检查点击位置是否在任何选中元素的控制点上
    // 这个检查应该在任何其他点击处理逻辑之前执行
    if (canvasMode === CanvasMode.SELECT) {
      // 获取当前选中元素
      const selectedIndices = selectionService.getSelectedIndices();
      const selectedTextIndices = selectionService.getSelectedTextIndices();
      const selectedCardIndices = selectionService.getSelectedCardIndices();
      const selectedImageIndices = selectionService.getSelectedImageIndices();
      
      // 检查所有选中的文本元素控制点
      for (const textIndex of selectedTextIndices) {
        const text = textService.getTexts()[textIndex];
        if (!text) continue;
        
        // 检查该文本是否属于任何选中的组合
        let isInSelectedGroup = false;
        if (groupService) {
          const groupId = groupService.isElementInAnyGroup('text', textIndex);
          isInSelectedGroup = groupId !== '' && selectionService.getSelectedGroupIds().includes(groupId);
        }
        
        // 如果元素属于被选中的组合，则跳过对该元素控制点的检测
        if (isInSelectedGroup) continue;
        
        const bounds = {
          minX: text.boxX ?? 0,
          minY: text.boxY ?? 0,
          maxX: (text.boxX ?? 0) + (text.boxWidth ?? 0),
          maxY: (text.boxY ?? 0) + (text.boxHeight ?? 0)
        };
        
        // 检查是否点击在控制点上
        const controlPointPosition = selectionService.isPointInControlPoint(point, bounds);
        if (controlPointPosition !== null) {
          // 设置激活的控制点
          selectionService.setActiveControlPoint(controlPointPosition);
          // 记录正在拖动控制点的状态
          setIsDraggingControlPoint(true);
          setActiveControlPointPosition(controlPointPosition);
          // 重新渲染画布以显示激活状态
          render();
          return;
        }
      }
      
      // 检查所有选中的卡片元素控制点
      for (const cardIndex of selectedCardIndices) {
        const card = cardService.getCards()[cardIndex];
        if (!card) continue;
        
        // 检查该卡片是否属于任何选中的组合
        let isInSelectedGroup = false;
        if (groupService) {
          const groupId = groupService.isElementInAnyGroup('card', cardIndex);
          isInSelectedGroup = groupId !== '' && selectionService.getSelectedGroupIds().includes(groupId);
        }
        
        // 如果元素属于被选中的组合，则跳过对该元素控制点的检测
        if (isInSelectedGroup) continue;
        
        const bounds = {
          minX: card.x,
          minY: card.y,
          maxX: card.x + card.width,
          maxY: card.y + card.height
        };
        
        // 检查是否点击在控制点上
        const controlPointPosition = selectionService.isPointInControlPoint(point, bounds);
        if (controlPointPosition !== null) {
          // 设置激活的控制点
          selectionService.setActiveControlPoint(controlPointPosition);
          // 记录正在拖动控制点的状态
          setIsDraggingControlPoint(true);
          setActiveControlPointPosition(controlPointPosition);
          // 重新渲染画布以显示激活状态
          render();
          return;
        }
      }
      
      // 检查所有选中的图片元素控制点
      for (const imageIndex of selectedImageIndices) {
        const image = imageService.getImages()[imageIndex];
        if (!image) continue;
        
        // 检查该图片是否属于任何选中的组合
        let isInSelectedGroup = false;
        if (groupService) {
          const groupId = groupService.isElementInAnyGroup('image', imageIndex);
          isInSelectedGroup = groupId !== '' && selectionService.getSelectedGroupIds().includes(groupId);
        }
        
        // 如果元素属于被选中的组合，则跳过对该元素控制点的检测
        if (isInSelectedGroup) continue;
        
        // 使用图片的精确边界，无边距
        const bounds = {
          minX: image.x,
          minY: image.y,
          maxX: image.x + image.width,
          maxY: image.y + image.height
        };
        
        // 检查是否点击在控制点上
        const controlPointPosition = selectionService.isPointInControlPoint(point, bounds);
        if (controlPointPosition !== null) {
          // 设置激活的控制点
          selectionService.setActiveControlPoint(controlPointPosition);
          // 记录正在拖动控制点的状态
          setIsDraggingControlPoint(true);
          setActiveControlPointPosition(controlPointPosition);
          // 重新渲染画布以显示激活状态
          render();
          return;
        }
      }
      
      // 检查所有选中的线条元素控制点
      for (const lineIndex of selectedIndices) {
        const line = drawingService.getLines()[lineIndex];
        if (!line || !line.points || line.points.length === 0) continue;
        
        // 检查该线条是否属于任何选中的组合
        let isInSelectedGroup = false;
        if (groupService) {
          const groupId = groupService.isElementInAnyGroup('line', lineIndex);
          isInSelectedGroup = groupId !== '' && selectionService.getSelectedGroupIds().includes(groupId);
        }
        
        // 如果元素属于被选中的组合，则跳过对该元素控制点的检测
        if (isInSelectedGroup) continue;
        
        // 获取线条边界
        const lineBounds = selectionService.getLineBoundingBox(line);
        
        // 检查是否点击在控制点上
        const controlPointPosition = selectionService.isPointInControlPoint(point, lineBounds);
        if (controlPointPosition !== null) {
          // 设置激活的控制点
          selectionService.setActiveControlPoint(controlPointPosition);
          // 记录正在拖动控制点的状态
          setIsDraggingControlPoint(true);
          setActiveControlPointPosition(controlPointPosition);
          // 重新渲染画布以显示激活状态
          render();
          return;
        }
      }
      
      // 检查所有选中的组合框控制点
      const selectedGroupIds = selectionService.getSelectedGroupIds();
      if (selectedGroupIds.length > 0) {
        for (const groupId of selectedGroupIds) {
          if (!groupService) continue;
          
          // 获取组合中的元素
          const groupElements = groupService.getGroupElements(groupId);
          
          // 初始化组合边界框
          let groupBounds: Bounds = {
            minX: Number.MAX_VALUE,
            minY: Number.MAX_VALUE,
            maxX: -Number.MAX_VALUE,
            maxY: -Number.MAX_VALUE
          };
          
          // 检查组合是否包含图片
          let containsImage = false;
          
          // 计算组合边界框
          for (const element of groupElements) {
            if (element.type === 'line' && element.index < drawingService.getLines().length) {
              const line = drawingService.getLines()[element.index];
              const lineBounds = selectionService.getLineBoundingBox(line, false);
              groupBounds.minX = Math.min(groupBounds.minX, lineBounds.minX);
              groupBounds.minY = Math.min(groupBounds.minY, lineBounds.minY);
              groupBounds.maxX = Math.max(groupBounds.maxX, lineBounds.maxX);
              groupBounds.maxY = Math.max(groupBounds.maxY, lineBounds.maxY);
            } else if (element.type === 'text' && element.index < textService.getTexts().length) {
              const text = textService.getTexts()[element.index];
              // 创建文本边界
              const textBounds = {
                minX: text.boxX ?? 0,
                minY: text.boxY ?? 0,
                maxX: (text.boxX ?? 0) + (text.boxWidth ?? 0),
                maxY: (text.boxY ?? 0) + (text.boxHeight ?? 0)
              };
              groupBounds.minX = Math.min(groupBounds.minX, textBounds.minX);
              groupBounds.minY = Math.min(groupBounds.minY, textBounds.minY);
              groupBounds.maxX = Math.max(groupBounds.maxX, textBounds.maxX);
              groupBounds.maxY = Math.max(groupBounds.maxY, textBounds.maxY);
            } else if (element.type === 'card' && element.index < cardService.getCards().length) {
              const card = cardService.getCards()[element.index];
              const cardBounds = {
                minX: card.x,
                minY: card.y,
                maxX: card.x + card.width,
                maxY: card.y + card.height
              };
              groupBounds.minX = Math.min(groupBounds.minX, cardBounds.minX);
              groupBounds.minY = Math.min(groupBounds.minY, cardBounds.minY);
              groupBounds.maxX = Math.max(groupBounds.maxX, cardBounds.maxX);
              groupBounds.maxY = Math.max(groupBounds.maxY, cardBounds.maxY);
            } else if (element.type === 'image' && element.index < imageService.getImages().length) {
              // 标记该组合包含图片
              containsImage = true;
              const image = imageService.getImages()[element.index];
              const imageBounds = {
                minX: image.x,
                minY: image.y,
                maxX: image.x + image.width,
                maxY: image.y + image.height
              };
              groupBounds.minX = Math.min(groupBounds.minX, imageBounds.minX);
              groupBounds.minY = Math.min(groupBounds.minY, imageBounds.minY);
              groupBounds.maxX = Math.max(groupBounds.maxX, imageBounds.maxX);
              groupBounds.maxY = Math.max(groupBounds.maxY, imageBounds.maxY);
            }
          }
          
          // 如果组合包含图片，则不显示控制点
          if (containsImage) {
            continue;
          }
          
          // 添加边距
          const currentScale = viewportService.getScale();
          const padding = 5 / currentScale;
          const groupBoundsWithPadding = {
            minX: groupBounds.minX - padding,
            minY: groupBounds.minY - padding,
            maxX: groupBounds.maxX + padding,
            maxY: groupBounds.maxY + padding
          };
          
          // 检查是否点击在组合框控制点上
          const controlPointPosition = selectionService.isPointInControlPoint(point, groupBoundsWithPadding);
          if (controlPointPosition !== null) {
            // 设置激活的控制点
            selectionService.setActiveControlPoint(controlPointPosition);
            // 记录正在拖动控制点的状态
            setIsDraggingControlPoint(true);
            setActiveControlPointPosition(controlPointPosition);
            // 重新渲染画布以显示激活状态
            render();
            return;
          }
        }
      }
    }
    
    switch (canvasMode) {
      case CanvasMode.DRAW:
        setIsDrawing(true);
        drawingService.startDrawing(point);
        break;
        
      case CanvasMode.TEXT:
        // 文本工具模式：在点击位置添加文本
        const newText = textService.addText(point.x, point.y);
        if (newText) {
          // 为了确保新添加的文本能够接收键盘事件，强制聚焦于画布
          canvasRef.current?.focus();
        }
        break;
        
      case CanvasMode.SELECT:
        // 优先检查文本选择
        const clickedTextIndex = findClickedText(point);
        if (clickedTextIndex !== -1) {
          // 点击在文本上
          const selectedTextIndices = selectionService.getSelectedTextIndices();
          
          // 如果点击在尚未选中的文本上，且按住了shift键
          if (!selectedTextIndices.includes(clickedTextIndex) && e.shiftKey) {
            // 使用selectElement方法处理选择，添加到现有选择
            selectionService.selectElement('text', clickedTextIndex, true);
            selectionService.startMoving(point);
            return;
          }
          
          // 如果点击在尚未选中的文本上，且没按shift键
          if (!selectedTextIndices.includes(clickedTextIndex) && !e.shiftKey) {
            // 使用selectElement方法处理选择
            selectionService.selectElement('text', clickedTextIndex);
            selectionService.startMoving(point);
            return;
          }
          
          // 如果点击在已选中的文本上，开始移动
          if (selectedTextIndices.includes(clickedTextIndex)) {
            // 检查是否按下shift键
            if (e.shiftKey) {
              // 如果按下shift键，则尝试取消选中该文本
              selectionService.selectElement('text', clickedTextIndex, true);
              render();
              return;
            }
            
            // 检查是否双击
            const now = Date.now();
            const texts = textService.getTexts();
            const textData = texts[clickedTextIndex];
            const lastClickTime = (textData as any)._lastClickTime || 0;
            const timeDiff = now - lastClickTime;
            
            // 保存点击时间
            if (textData) {
              (textData as any)._lastClickTime = now;
            }
            
            // 如果是单击，且这是第二次单击（时间间隔小于500毫秒但大于10毫秒），进入编辑模式
            if (timeDiff < 500 && timeDiff > 10) {
              // 标记为编辑状态，但不再隐藏原文本
              textData.isEditing = true;
              
              // 清除选择服务中的选中状态，移除选择框
              selectionService.clearSelection();
              
              // 清除当前悬停的文本索引，防止显示提示框
              setHoveredTextIndex(-1);
              
              // 调用编辑方法，打开文本编辑模式，并传递点击坐标
              textService.editText(clickedTextIndex, e.clientX, e.clientY);
              
              render();
              return;
            }
            
            // 获取文本的边界框
            const text = texts[clickedTextIndex];
            const bounds = {
              minX: text.boxX ?? 0,
              minY: text.boxY ?? 0,
              maxX: (text.boxX ?? 0) + (text.boxWidth ?? 0),
              maxY: (text.boxY ?? 0) + (text.boxHeight ?? 0)
            };
            
            // 检查是否点击在控制点上
            const controlPointPosition = selectionService.isPointInControlPoint(point, bounds);
            if (controlPointPosition !== null) {
              // 设置激活的控制点
              selectionService.setActiveControlPoint(controlPointPosition);
              // 记录正在拖动控制点的状态
              setIsDraggingControlPoint(true);
              setActiveControlPointPosition(controlPointPosition);
              // 重新渲染画布以显示激活状态
              render();
              return;
            }
            
            selectionService.startMoving(point);
            return;
          }
        }
        
        // 如果没有点击在文本上，检查线条选择
        const selectedIndices = selectionService.getSelectedIndices();
        const selectedTextIndices = selectionService.getSelectedTextIndices();
        const selectedCardIndices = selectionService.getSelectedCardIndices();
        const selectedImageIndices = selectionService.getSelectedImageIndices();
        const lines = drawingService.getLines();
        const texts = textService.getTexts();
        
        // 检查点击是否在任何线条上
        const clickedLineIndex = findClickedLine(point, lines);
        
        // 如果点击在线条上
        if (clickedLineIndex !== -1) {
          // 如果有选中元素且按住了shift键
          if ((selectedIndices.length > 0 || selectedTextIndices.length > 0 || 
               selectedCardIndices.length > 0 || selectedImageIndices.length > 0) && e.shiftKey) {
            // 使用selectElement方法处理选择，添加到现有选择
            selectionService.selectElement('line', clickedLineIndex, true);
            selectionService.startMoving(point);
            return;
          }
          
          // 如果点击在尚未选中的线条上，且没按shift键
          if (!selectedIndices.includes(clickedLineIndex) && !e.shiftKey) {
            // 使用selectElement方法处理选择
            selectionService.selectElement('line', clickedLineIndex);
            selectionService.startMoving(point);
            return;
          }
          
          // 如果点击在已选中的线条上，开始移动
          if (selectedIndices.includes(clickedLineIndex)) {
            // 检查是否按下shift键
            if (e.shiftKey) {
              // 如果按下shift键，则尝试取消选中该线条
              selectionService.selectElement('line', clickedLineIndex, true);
              render();
              return;
            }
            
            selectionService.startMoving(point);
            return;
          }
        }
        
        // 检查点击是否在卡片上
        const clickedCardIndex = findClickedCard(point);
        
        // 如果点击在卡片上
        if (clickedCardIndex !== -1) {
          // 如果有选中元素且按住了shift键
          if ((selectedIndices.length > 0 || selectedTextIndices.length > 0 || 
               selectedCardIndices.length > 0 || selectedImageIndices.length > 0) && e.shiftKey) {
            // 使用selectElement方法处理选择，添加到现有选择
            selectionService.selectElement('card', clickedCardIndex, true);
            selectionService.startMoving(point);
            return;
          }
          
          // 如果点击在尚未选中的卡片上，且没按shift键
          if (!selectedCardIndices.includes(clickedCardIndex) && !e.shiftKey) {
            // 使用selectElement方法处理选择
            selectionService.selectElement('card', clickedCardIndex);
            selectionService.startMoving(point);
            return;
          }
          
          // 如果点击在已选中的卡片上，开始移动
          if (selectedCardIndices.includes(clickedCardIndex)) {
            // 检查是否按下shift键
            if (e.shiftKey) {
              // 如果按下shift键，则尝试取消选中该卡片
              selectionService.selectElement('card', clickedCardIndex, true);
              render();
              return;
            }
            
            // 获取卡片的边界框
            const card = cardService.getCards()[clickedCardIndex];
            const bounds = {
              minX: card.x,
              minY: card.y,
              maxX: card.x + card.width,
              maxY: card.y + card.height
            };
            
            // 检查是否点击在控制点上
            const controlPointPosition = selectionService.isPointInControlPoint(point, bounds);
            if (controlPointPosition !== null) {
              // 设置激活的控制点
              selectionService.setActiveControlPoint(controlPointPosition);
              // 记录正在拖动控制点的状态
              setIsDraggingControlPoint(true);
              setActiveControlPointPosition(controlPointPosition);
              // 重新渲染画布以显示激活状态
              render();
              return;
            }
            
            selectionService.startMoving(point);
            return;
          }
        }
        
        // 检查点击是否在图片上
        const clickedImageIndex = findClickedImage(point);
        
        // 如果点击在图片上
        if (clickedImageIndex !== -1) {
          // 如果有选中元素且按住了shift键
          if ((selectedIndices.length > 0 || selectedTextIndices.length > 0 || 
               selectedCardIndices.length > 0 || selectedImageIndices.length > 0) && e.shiftKey) {
            // 使用selectElement方法处理选择，添加到现有选择
            selectionService.selectElement('image', clickedImageIndex, true);
            selectionService.startMoving(point);
            return;
          }
          
          // 如果点击在尚未选中的图片上，且没按shift键
          if (!selectedImageIndices.includes(clickedImageIndex) && !e.shiftKey) {
            // 使用selectElement方法处理选择
            selectionService.selectElement('image', clickedImageIndex);
            selectionService.startMoving(point);
            return;
          }
          
          // 如果点击在已选中的图片上，开始移动
          if (selectedImageIndices.includes(clickedImageIndex)) {
            // 检查是否按下shift键
            if (e.shiftKey) {
              // 如果按下shift键，则尝试取消选中该图片
              selectionService.selectElement('image', clickedImageIndex, true);
              render();
              return;
            }
            
            // 获取图片的边界框
            const image = imageService.getImages()[clickedImageIndex];
            const bounds = {
              minX: image.x,
              minY: image.y,
              maxX: image.x + image.width,
              maxY: image.y + image.height
            };
            
            // 检查是否点击在控制点上
            const controlPointPosition = selectionService.isPointInControlPoint(point, bounds);
            if (controlPointPosition !== null) {
              // 设置激活的控制点
              selectionService.setActiveControlPoint(controlPointPosition);
              // 记录正在拖动控制点的状态
              setIsDraggingControlPoint(true);
              setActiveControlPointPosition(controlPointPosition);
              // 重新渲染画布以显示激活状态
              render();
              return;
            }
            
            selectionService.startMoving(point);
            return;
          }
        }
        
        // 如果有选中的元素
        if (selectedIndices.length > 0 || selectedTextIndices.length > 0 || selectedCardIndices.length > 0 || selectedImageIndices.length > 0) {
          // 检查点是否在选中内容的边界框内，如果在，则可以移动
          if (selectionService.isPointInSelectionBounds(point, lines, texts, cardService.getCards(), imageService.getImages())) {
            selectionService.startMoving(point);
            return;
          }
          
          // 如果点击不在任何元素上，且不按shift，则清除选择并开始新选择
          if (clickedLineIndex === -1 && clickedTextIndex === -1 && clickedCardIndex === -1 && clickedImageIndex === -1 && !e.shiftKey) {
            // 检查是否点击在任何选中元素的控制点上
            let isPointInControlPoint = false;
            
            // 检查所有选中文本元素的控制点
            for (const index of selectedTextIndices) {
              const text = textService.getTexts()[index];
              if (!text) continue;
              
              const bounds = {
                minX: text.boxX ?? 0,
                minY: text.boxY ?? 0,
                maxX: (text.boxX ?? 0) + (text.boxWidth ?? 0),
                maxY: (text.boxY ?? 0) + (text.boxHeight ?? 0)
              };
              
              if (selectionService.isPointInControlPoint(point, bounds)) {
                isPointInControlPoint = true;
                break;
              }
            }
            
            // 检查所有选中卡片元素的控制点
            if (!isPointInControlPoint) {
              for (const index of selectedCardIndices) {
                const card = cardService.getCards()[index];
                if (!card) continue;
                
                const bounds = {
                  minX: card.x,
                  minY: card.y,
                  maxX: card.x + card.width,
                  maxY: card.y + card.height
                };
                
                if (selectionService.isPointInControlPoint(point, bounds)) {
                  isPointInControlPoint = true;
                  break;
                }
              }
            }
            
            // 检查所有选中图片元素的控制点
            if (!isPointInControlPoint) {
              for (const index of selectedImageIndices) {
                const image = imageService.getImages()[index];
                if (!image) continue;
                
                // 使用图片的精确边界，无边距
                const bounds = {
                  minX: image.x,
                  minY: image.y,
                  maxX: image.x + image.width,
                  maxY: image.y + image.height
                };
                
                if (selectionService.isPointInControlPoint(point, bounds)) {
                  isPointInControlPoint = true;
                  break;
                }
              }
            }
            
            // 检查所有选中线条元素的控制点
            if (!isPointInControlPoint) {
              for (const index of selectedIndices) {
                const line = drawingService.getLines()[index];
                if (!line || !line.points || line.points.length === 0) continue;
                
                const bounds = selectionService.getLineBoundingBox(line);
                
                if (selectionService.isPointInControlPoint(point, bounds)) {
                  isPointInControlPoint = true;
                  break;
                }
              }
            }
            
            // 只有当点击不在任何控制点上时，才清除选择
            if (!isPointInControlPoint) {
              // 清除激活的控制点
              selectionService.setActiveControlPoint(null);
              setActiveControlPointPosition(null);
              setIsDraggingControlPoint(false);
              
              selectionService.clearSelection();
              selectionService.startSelection(point);
            }
            return;
          }
          
          // 如果点击不在任何元素上，且按shift，则保留现有选择并开始新选择
          if (clickedLineIndex === -1 && clickedTextIndex === -1 && clickedCardIndex === -1 && clickedImageIndex === -1 && e.shiftKey) {
            selectionService.startSelection(point);
            return;
          }
          
        } else {
          // 尚未有选中的元素
          
          // 如果点击在线条上，选中该线条
          if (clickedLineIndex !== -1) {
            selectionService.setSelectedIndices([clickedLineIndex]);
            selectionService.startMoving(point);
            return;
          }
          
          // 如果点击在卡片上，选中该卡片
          if (clickedCardIndex !== -1) {
            selectionService.setSelectedCardIndices([clickedCardIndex]);
            selectionService.startMoving(point);
            return;
          }
          
          // 如果点击在图片上，选中该图片
          if (clickedImageIndex !== -1) {
            selectionService.setSelectedImageIndices([clickedImageIndex]);
            selectionService.startMoving(point);
            return;
          }
          
          // 如果不在任何元素上，开始框选
          selectionService.startSelection(point);
          return;
        }
        break;
        
      case CanvasMode.SHAPE:
        setIsDrawing(true);
        drawingService.startShape(point);
        break;
        
      case CanvasMode.CARD:
        // 在点击位置添加新卡片
        cardService.addCard(point.x, point.y);
        render();
        break;
        
      case CanvasMode.IMAGE:
        // 当用户在画布上点击时触发文件选择
        // 创建并触发一个自定义事件，通知ImageTool组件
        // 传递点击位置信息，以便在该位置插入图片
        const imageUploadEvent = new CustomEvent('canvas-image-upload', {
          detail: { x: point.x, y: point.y }
        });
        document.dispatchEvent(imageUploadEvent);
        break;
    }
    
    render();
  }, [
    canvasMode, 
    drawingService, 
    selectionService, 
    viewportService, 
    isSpacePressed, 
    getCanvasPoint, 
    setContextMenuState,
    render,
    cardService,
    completeLineSelection,
    isFixed,
    findClickedImage
  ]);

  // 将handleGlobalMouseMove提取到useEffect外部定义
  const handleGlobalMouseMove = useCallback((event: MouseEvent) => {
    // 处理视口拖动 - 无论是否在绘制状态，但在固定模式下不允许拖动
    if ((isSpacePressed || isRightDragging) && viewportService.isDraggingViewport() && !isFixed) {
      viewportService.drag(event.clientX, event.clientY);
      render();
      
      // 如果不是右键拖动，直接返回
      // 右键拖动时，继续处理绘图状态，以便能够同时拖动视图并保持绘制状态
      if (!isRightDragging) {
        return;
      }
    }
    
    if (!isDrawing && !selectionService.isSelecting() && !selectionService.isContentMoving() && !isDraggingControlPoint) {
      return;
    }
    
    // 只有在绘制、选择、移动模式或拖动控制点时才处理
    if (canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const point = viewportService.clientToCanvas(x, y);
      
      // 如果是右键拖动，则不更新绘制状态
      if (isRightDragging) {
        render();
        return;
      }
      
      // 添加对控制点拖动的处理
      if (isDraggingControlPoint && activeControlPointPosition) {
        // 实现控制点拖动的逻辑
        
        // 获取选中元素并调整其大小
        const selectedIndices = selectionService.getSelectedIndices();
        const selectedTextIndices = selectionService.getSelectedTextIndices();
        const selectedCardIndices = selectionService.getSelectedCardIndices();
        const selectedImageIndices = selectionService.getSelectedImageIndices();
        
        // 示例：调整文本元素大小
        if (selectedTextIndices.length === 1) {
          const textIndex = selectedTextIndices[0];
          const text = textService.getTexts()[textIndex];
          if (text) {
            // 根据拖动方向和控制点位置调整文本大小
            let newWidth = text.boxWidth || 0;
            let newHeight = text.boxHeight || 0;
            
            // 根据拖动的控制点位置调整大小
            if (activeControlPointPosition === 'BOTTOM_RIGHT') {
              newWidth = Math.max(50, point.x - (text.boxX || 0));
              newHeight = Math.max(50, point.y - (text.boxY || 0));
            } else if (activeControlPointPosition === 'TOP_RIGHT') {
              newWidth = Math.max(50, point.x - (text.boxX || 0));
              // TODO: 实现其他调整
            } else if (activeControlPointPosition === 'BOTTOM_LEFT') {
              // TODO: 实现其他调整
            } else if (activeControlPointPosition === 'TOP_LEFT') {
              // TODO: 实现其他调整
            }
            
            // 应用调整的大小
            text.boxWidth = newWidth;
            text.boxHeight = newHeight;
          }
        }
        
        // 示例：调整卡片元素大小
        if (selectedCardIndices.length === 1) {
          const cardIndex = selectedCardIndices[0];
          const card = cardService.getCards()[cardIndex];
          if (card) {
            // 根据拖动方向和控制点位置调整卡片大小
            let newWidth = card.width;
            let newHeight = card.height;
            
            // 根据拖动的控制点位置调整大小
            if (activeControlPointPosition === 'BOTTOM_RIGHT') {
              newWidth = Math.max(50, point.x - card.x);
              newHeight = Math.max(50, point.y - card.y);
            } else if (activeControlPointPosition === 'TOP_RIGHT') {
              newWidth = Math.max(50, point.x - card.x);
              // TODO: 实现其他调整
            } else if (activeControlPointPosition === 'BOTTOM_LEFT') {
              // TODO: 实现其他调整
            } else if (activeControlPointPosition === 'TOP_LEFT') {
              // TODO: 实现其他调整
            }
            
            // 应用调整的大小
            card.width = newWidth;
            card.height = newHeight;
          }
        }
        
        // 示例：调整图片元素大小
        if (selectedImageIndices.length === 1) {
          const imageIndex = selectedImageIndices[0];
          const image = imageService.getImages()[imageIndex];
          if (image) {
            // 获取当前图片的边界 - 精确边界，不添加边距
            const oldX = image.x;
            const oldY = image.y;
            const oldWidth = image.width;
            const oldHeight = image.height;
            const oldRight = oldX + oldWidth;
            const oldBottom = oldY + oldHeight;
            const oldCenterX = oldX + oldWidth / 2;
            const oldCenterY = oldY + oldHeight / 2;
            
            // 使用图片的宽高比 - 单个图片始终保持等比例缩放
            const aspectRatio = image.aspectRatio || (oldWidth / oldHeight);
            
            // 根据拖动的控制点位置调整大小，保持宽高比
            let newWidth = oldWidth;
            let newHeight = oldHeight;
            let newX = oldX;
            let newY = oldY;
            
            switch (activeControlPointPosition) {
              case 'BOTTOM_RIGHT':
                // 左上角固定，调整右下角
                // 计算两个方向的新尺寸
                const widthFromX = Math.max(50, point.x - oldX);
                const heightFromY = Math.max(50, point.y - oldY);
                
                // 分别计算保持宽高比的两个尺寸方案
                const heightFromXRatio = widthFromX / aspectRatio;
                const widthFromYRatio = heightFromY * aspectRatio;
                
                // 选择较大的方案，确保图片扩展到足够大
                if (widthFromX >= widthFromYRatio) {
                  // 以水平方向为准
                  newWidth = widthFromX;
                  newHeight = heightFromXRatio;
                } else {
                  // 以垂直方向为准
                  newWidth = widthFromYRatio;
                  newHeight = heightFromY;
                }
                break;
                
              case 'TOP_LEFT':
                // 右下角固定，调整左上角
                const widthFromTL = Math.max(50, oldRight - point.x);
                const heightFromTL = Math.max(50, oldBottom - point.y);
                
                // 保持宽高比的两个尺寸方案
                const heightFromWidthTL = widthFromTL / aspectRatio;
                const widthFromHeightTL = heightFromTL * aspectRatio;
                
                if (widthFromTL >= widthFromHeightTL) {
                  // 以水平方向为准
                  newWidth = widthFromTL;
                  newHeight = heightFromWidthTL;
                  newX = oldRight - newWidth;
                  newY = oldBottom - newHeight;
                } else {
                  // 以垂直方向为准
                  newWidth = widthFromHeightTL;
                  newHeight = heightFromTL;
                  newX = oldRight - newWidth;
                  newY = oldBottom - newHeight;
                }
                break;
                
              case 'TOP_RIGHT':
                // 左下角固定，调整右上角
                const widthFromTR = Math.max(50, point.x - oldX);
                const heightFromTR = Math.max(50, oldBottom - point.y);
                
                // 保持宽高比的两个尺寸方案
                const heightFromWidthTR = widthFromTR / aspectRatio;
                const widthFromHeightTR = heightFromTR * aspectRatio;
                
                if (widthFromTR >= widthFromHeightTR) {
                  // 以水平方向为准
                  newWidth = widthFromTR;
                  newHeight = heightFromWidthTR;
                  newY = oldBottom - newHeight;
                } else {
                  // 以垂直方向为准
                  newWidth = widthFromHeightTR;
                  newHeight = heightFromTR;
                  newY = oldBottom - newHeight;
                }
                break;
                
              case 'BOTTOM_LEFT':
                // 右上角固定，调整左下角
                const widthFromBL = Math.max(50, oldRight - point.x);
                const heightFromBL = Math.max(50, point.y - oldY);
                
                // 保持宽高比的两个尺寸方案
                const heightFromWidthBL = widthFromBL / aspectRatio;
                const widthFromHeightBL = heightFromBL * aspectRatio;
                
                if (widthFromBL >= widthFromHeightBL) {
                  // 以水平方向为准
                  newWidth = widthFromBL;
                  newHeight = heightFromWidthBL;
                  newX = oldRight - newWidth;
                } else {
                  // 以垂直方向为准
                  newWidth = widthFromHeightBL;
                  newHeight = heightFromBL;
                  newX = oldRight - newWidth;
                }
                break;
              
              case 'TOP_CENTER':
                // 底边中心固定，调整高度，保持水平居中
                newHeight = Math.max(50, oldBottom - point.y);
                newWidth = newHeight * aspectRatio;
                newX = oldCenterX - newWidth / 2;
                newY = oldBottom - newHeight;
                break;
              
              case 'BOTTOM_CENTER':
                // 顶边中心固定，调整高度，保持水平居中
                newHeight = Math.max(50, point.y - oldY);
                newWidth = newHeight * aspectRatio;
                newX = oldCenterX - newWidth / 2;
                break;
              
              case 'LEFT_CENTER':
                // 右边中点固定，调整宽度和高度，保持垂直居中
                newWidth = Math.max(50, oldRight - point.x);
                newHeight = newWidth / aspectRatio;
                newX = point.x; // 确保左边界对齐鼠标
                newY = oldCenterY - newHeight / 2;
                break;
              
              case 'RIGHT_CENTER':
                // 左边中点固定，调整宽度和高度，保持垂直居中
                newWidth = Math.max(50, point.x - oldX);
                newHeight = newWidth / aspectRatio;
                // 右边界已经对齐鼠标，不需要额外调整
                newY = oldCenterY - newHeight / 2;
                break;
            }
            
            // 应用调整的大小和位置
            image.x = newX;
            image.y = newY;
            imageService.resizeImage(imageIndex, newWidth, newHeight);
            
            // 触发渲染
            render();
          }
        }
        
        // 示例：调整线条元素大小
        if (selectedIndices.length === 1) {
          const lineIndex = selectedIndices[0];
          const line = drawingService.getLines()[lineIndex];
          if (line && line.points && line.points.length > 0) {
            // 获取线条边界
            const lineBounds = selectionService.getLineBoundingBox(line, false);
            const oldMinX = lineBounds.minX;
            const oldMinY = lineBounds.minY;
            const oldMaxX = lineBounds.maxX;
            const oldMaxY = lineBounds.maxY;
            const oldWidth = oldMaxX - oldMinX;
            const oldHeight = oldMaxY - oldMinY;
            
            // 计算缩放比例
            let scaleX = 1;
            let scaleY = 1;
            let translateX = 0;
            let translateY = 0;
            
            // 检查是否按下shift键，用于等比例缩放
            const isShiftPressed = event.shiftKey;
            // 计算原始宽高比
            const aspectRatio = oldWidth / oldHeight;
            
            // 定义最小尺寸限制，防止元素被压缩得过小导致不稳定
            const minWidth = Math.max(10, line.size * 3); // 至少是线条粗细的3倍或10像素
            const minHeight = Math.max(10, line.size * 3);
            const minScaleX = minWidth / oldWidth;
            const minScaleY = minHeight / oldHeight;
            
            switch (activeControlPointPosition) {
              case 'TOP_LEFT':
                // 计算从右下角到鼠标位置的宽高
                const widthTL = oldMaxX - point.x;
                const heightTL = oldMaxY - point.y;
                
                // 应用最小尺寸限制
                const limitedWidthTL = Math.max(widthTL, minWidth);
                const limitedHeightTL = Math.max(heightTL, minHeight);
                
                // 如果达到最小限制，重新计算鼠标位置以避免推动效果
                if (widthTL < minWidth) {
                  point.x = oldMaxX - minWidth;
                }
                if (heightTL < minHeight) {
                  point.y = oldMaxY - minHeight;
                }
                
                scaleX = (oldMaxX - point.x) / oldWidth;
                scaleY = (oldMaxY - point.y) / oldHeight;

                // 等比例缩放
                if (isShiftPressed) {
                  // 选择较大的缩放比例，保持宽高比
                  if (Math.abs(scaleX) > Math.abs(scaleY)) {
                    scaleY = scaleX;
                    // 重新调整点y坐标
                    point.y = oldMaxY - scaleY * oldHeight;
                  } else {
                    scaleX = scaleY;
                    // 重新调整点x坐标
                    point.x = oldMaxX - scaleX * oldWidth;
                  }
                }
                
                translateX = point.x - oldMinX;
                translateY = point.y - oldMinY;
                break;
              case 'TOP_RIGHT':
                // 计算从左下角到鼠标位置的宽高
                const widthTR = point.x - oldMinX;
                const heightTR = oldMaxY - point.y;
                
                // 应用最小尺寸限制
                const limitedWidthTR = Math.max(widthTR, minWidth);
                const limitedHeightTR = Math.max(heightTR, minHeight);
                
                // 如果达到最小限制，重新计算鼠标位置以避免推动效果
                if (widthTR < minWidth) {
                  point.x = oldMinX + minWidth;
                }
                if (heightTR < minHeight) {
                  point.y = oldMaxY - minHeight;
                }
                
                scaleX = (point.x - oldMinX) / oldWidth;
                scaleY = (oldMaxY - point.y) / oldHeight;
                
                // 等比例缩放
                if (isShiftPressed) {
                  // 选择较大的缩放比例，保持宽高比
                  if (Math.abs(scaleX) > Math.abs(scaleY)) {
                    scaleY = scaleX;
                    // 重新调整点y坐标
                    point.y = oldMaxY - scaleY * oldHeight;
                  } else {
                    scaleX = scaleY;
                    // 重新调整点x坐标
                    point.x = oldMinX + scaleX * oldWidth;
                  }
                }
                
                translateY = point.y - oldMinY;
                break;
              case 'BOTTOM_LEFT':
                // 计算从右上角到鼠标位置的宽高
                const widthBL = oldMaxX - point.x;
                const heightBL = point.y - oldMinY;
                
                // 应用最小尺寸限制
                const limitedWidthBL = Math.max(widthBL, minWidth);
                const limitedHeightBL = Math.max(heightBL, minHeight);
                
                // 如果达到最小限制，重新计算鼠标位置以避免推动效果
                if (widthBL < minWidth) {
                  point.x = oldMaxX - minWidth;
                }
                if (heightBL < minHeight) {
                  point.y = oldMinY + minHeight;
                }
                
                scaleX = (oldMaxX - point.x) / oldWidth;
                scaleY = (point.y - oldMinY) / oldHeight;
                
                // 等比例缩放
                if (isShiftPressed) {
                  // 选择较大的缩放比例，保持宽高比
                  if (Math.abs(scaleX) > Math.abs(scaleY)) {
                    scaleY = scaleX;
                    // 重新调整点y坐标
                    point.y = oldMinY + scaleY * oldHeight;
                  } else {
                    scaleX = scaleY;
                    // 重新调整点x坐标
                    point.x = oldMaxX - scaleX * oldWidth;
                  }
                }
                
                translateX = point.x - oldMinX;
                break;
              case 'BOTTOM_RIGHT':
                // 计算从左上角到鼠标位置的宽高
                const widthBR = point.x - oldMinX;
                const heightBR = point.y - oldMinY;
                
                // 应用最小尺寸限制
                const limitedWidthBR = Math.max(widthBR, minWidth);
                const limitedHeightBR = Math.max(heightBR, minHeight);
                
                // 如果达到最小限制，重新计算鼠标位置以避免推动效果
                if (widthBR < minWidth) {
                  point.x = oldMinX + minWidth;
                }
                if (heightBR < minHeight) {
                  point.y = oldMinY + minHeight;
                }
                
                scaleX = (point.x - oldMinX) / oldWidth;
                scaleY = (point.y - oldMinY) / oldHeight;
                
                // 等比例缩放
                if (isShiftPressed) {
                  // 选择较大的缩放比例，保持宽高比
                  if (Math.abs(scaleX) > Math.abs(scaleY)) {
                    scaleY = scaleX;
                    // 重新调整点y坐标
                    point.y = oldMinY + scaleY * oldHeight;
                  } else {
                    scaleX = scaleY;
                    // 重新调整点x坐标
                    point.x = oldMinX + scaleX * oldWidth;
                  }
                }
                break;
              case 'TOP_CENTER':
                // 应用最小高度限制
                if (oldMaxY - point.y < minHeight) {
                  point.y = oldMaxY - minHeight;
                }
                
                scaleY = (oldMaxY - point.y) / oldHeight;
                
                // 等比例缩放
                if (isShiftPressed) {
                  scaleX = scaleY;
                  // 保持水平居中
                  translateX = -oldWidth * (scaleX - 1) / 2;
                }
                translateY = point.y - oldMinY;
                break;
              case 'BOTTOM_CENTER':
                // 应用最小高度限制
                if (point.y - oldMinY < minHeight) {
                  point.y = oldMinY + minHeight;
                }
                
                scaleY = (point.y - oldMinY) / oldHeight;
                
                // 等比例缩放
                if (isShiftPressed) {
                  scaleX = scaleY;
                  // 保持水平居中
                  translateX = -oldWidth * (scaleX - 1) / 2;
                }
                break;
              case 'LEFT_CENTER':
                // 应用最小宽度限制
                if (oldMaxX - point.x < minWidth) {
                  point.x = oldMaxX - minWidth;
                }
                
                scaleX = (oldMaxX - point.x) / oldWidth;
                
                // 等比例缩放
                if (isShiftPressed) {
                  scaleY = scaleX;
                  // 保持垂直居中
                  translateY = -oldHeight * (scaleY - 1) / 2;
                }
                translateX = point.x - oldMinX;
                break;
              case 'RIGHT_CENTER':
                // 应用最小宽度限制
                if (point.x - oldMinX < minWidth) {
                  point.x = oldMinX + minWidth;
                }
                
                scaleX = (point.x - oldMinX) / oldWidth;
                
                // 等比例缩放
                if (isShiftPressed) {
                  scaleY = scaleX;
                  // 保持垂直居中
                  translateY = -oldHeight * (scaleY - 1) / 2;
                }
                break;
            }
            
            // 确保缩放比例不会太小，但现在这是一个备用检查，因为我们已经限制了鼠标位置
            scaleX = Math.max(minScaleX, scaleX);
            scaleY = Math.max(minScaleY, scaleY);
            
            // 应用变换到线条的每个点
            const newPoints = line.points.map(p => {
              // 计算点相对于边界的位置
              const relativeX = p.x - oldMinX;
              const relativeY = p.y - oldMinY;
              
              // 应用缩放和平移
              return {
                x: oldMinX + relativeX * scaleX + translateX,
                y: oldMinY + relativeY * scaleY + translateY
              };
            });
            
            // 更新线条的点
            drawingService.updateLinePoints(lineIndex, newPoints);
            
            // 重绘画布
            render();
          }
        }
        
        // 处理组合框的缩放
        const selectedGroupIds = selectionService.getSelectedGroupIds();
        if (selectedGroupIds.length > 0) {
          // 使用第一个组合作为当前处理的组合
          const groupId = selectedGroupIds[0];
          if (groupService) {
            // 获取组合中的元素
            const groupElements = groupService.getGroupElements(groupId);
            if (groupElements.length > 0) {
              // 计算组合边界框
              let groupBounds: Bounds = {
                minX: Number.MAX_VALUE,
                minY: Number.MAX_VALUE,
                maxX: -Number.MAX_VALUE,
                maxY: -Number.MAX_VALUE
              };
              
              // 检查组合中是否包含图片
              let containsImage = false;
              
              // 计算组合边界框，不包含内边距
              for (const element of groupElements) {
                if (element.type === 'line' && element.index < drawingService.getLines().length) {
                  const line = drawingService.getLines()[element.index];
                  const lineBounds = selectionService.getLineBoundingBox(line, false);
                  groupBounds.minX = Math.min(groupBounds.minX, lineBounds.minX);
                  groupBounds.minY = Math.min(groupBounds.minY, lineBounds.minY);
                  groupBounds.maxX = Math.max(groupBounds.maxX, lineBounds.maxX);
                  groupBounds.maxY = Math.max(groupBounds.maxY, lineBounds.maxY);
                } else if (element.type === 'text' && element.index < textService.getTexts().length) {
                  const text = textService.getTexts()[element.index];
                  // 创建文本边界
                  const textBounds = {
                    minX: text.boxX ?? 0,
                    minY: text.boxY ?? 0,
                    maxX: (text.boxX ?? 0) + (text.boxWidth ?? 0),
                    maxY: (text.boxY ?? 0) + (text.boxHeight ?? 0)
                  };
                  groupBounds.minX = Math.min(groupBounds.minX, textBounds.minX);
                  groupBounds.minY = Math.min(groupBounds.minY, textBounds.minY);
                  groupBounds.maxX = Math.max(groupBounds.maxX, textBounds.maxX);
                  groupBounds.maxY = Math.max(groupBounds.maxY, textBounds.maxY);
                } else if (element.type === 'card' && element.index < cardService.getCards().length) {
                  const card = cardService.getCards()[element.index];
                  const cardBounds = {
                    minX: card.x,
                    minY: card.y,
                    maxX: card.x + card.width,
                    maxY: card.y + card.height
                  };
                  groupBounds.minX = Math.min(groupBounds.minX, cardBounds.minX);
                  groupBounds.minY = Math.min(groupBounds.minY, cardBounds.minY);
                  groupBounds.maxX = Math.max(groupBounds.maxX, cardBounds.maxX);
                  groupBounds.maxY = Math.max(groupBounds.maxY, cardBounds.maxY);
                } else if (element.type === 'image' && element.index < imageService.getImages().length) {
                  // 标记组合中包含图片
                  containsImage = true;
                  const image = imageService.getImages()[element.index];
                  const imageBounds = {
                    minX: image.x,
                    minY: image.y,
                    maxX: image.x + image.width,
                    maxY: image.y + image.height
                  };
                  groupBounds.minX = Math.min(groupBounds.minX, imageBounds.minX);
                  groupBounds.minY = Math.min(groupBounds.minY, imageBounds.minY);
                  groupBounds.maxX = Math.max(groupBounds.maxX, imageBounds.maxX);
                  groupBounds.maxY = Math.max(groupBounds.maxY, imageBounds.maxY);
                }
              }
              
              // 如果组合中包含图片，则禁止缩放
              if (containsImage) {
                // 跳过缩放操作
                return;
              }
              
              // 获取组合框的原始尺寸
              const oldMinX = groupBounds.minX;
              const oldMinY = groupBounds.minY;
              const oldMaxX = groupBounds.maxX;
              const oldMaxY = groupBounds.maxY;
              const oldWidth = oldMaxX - oldMinX;
              const oldHeight = oldMaxY - oldMinY;
              
              // 计算缩放比例
              let scaleX = 1;
              let scaleY = 1;
              let translateX = 0;
              let translateY = 0;
              
              // 检查是否按下shift键，用于等比例缩放
              // 如果组合中包含图片，则强制使用等比例缩放
              const isShiftPressed = event.shiftKey || containsImage;
              // 计算原始宽高比
              const aspectRatio = oldWidth / oldHeight;
              
              // 定义最小尺寸限制，防止元素被压缩得过小
              const minWidth = 20;
              const minHeight = 20;
              const minScaleX = minWidth / oldWidth;
              const minScaleY = minHeight / oldHeight;
              
              // 根据拖动的控制点位置计算缩放比例
              switch (activeControlPointPosition) {
                case 'TOP_LEFT':
                  // 计算从右下角到鼠标位置的宽高
                  const widthTL = oldMaxX - point.x;
                  const heightTL = oldMaxY - point.y;
                  
                  // 应用最小尺寸限制
                  const limitedWidthTL = Math.max(widthTL, minWidth);
                  const limitedHeightTL = Math.max(heightTL, minHeight);
                  
                  // 如果达到最小限制，重新计算鼠标位置以避免推动效果
                  if (widthTL < minWidth) {
                    point.x = oldMaxX - minWidth;
                  }
                  if (heightTL < minHeight) {
                    point.y = oldMaxY - minHeight;
                  }
                  
                  scaleX = (oldMaxX - point.x) / oldWidth;
                  scaleY = (oldMaxY - point.y) / oldHeight;
                  
                  // 等比例缩放
                  if (isShiftPressed) {
                    // 选择较大的缩放比例，保持宽高比
                    if (Math.abs(scaleX) > Math.abs(scaleY)) {
                      scaleY = scaleX;
                      // 重新调整点y坐标
                      point.y = oldMaxY - scaleY * oldHeight;
                    } else {
                      scaleX = scaleY;
                      // 重新调整点x坐标
                      point.x = oldMaxX - scaleX * oldWidth;
                    }
                  }
                  
                  translateX = point.x - oldMinX;
                  translateY = point.y - oldMinY;
                  break;
                case 'TOP_RIGHT':
                  // 计算从左下角到鼠标位置的宽高
                  const widthTR = point.x - oldMinX;
                  const heightTR = oldMaxY - point.y;
                  
                  // 应用最小尺寸限制
                  const limitedWidthTR = Math.max(widthTR, minWidth);
                  const limitedHeightTR = Math.max(heightTR, minHeight);
                  
                  // 如果达到最小限制，重新计算鼠标位置以避免推动效果
                  if (widthTR < minWidth) {
                    point.x = oldMinX + minWidth;
                  }
                  if (heightTR < minHeight) {
                    point.y = oldMaxY - minHeight;
                  }
                  
                  scaleX = (point.x - oldMinX) / oldWidth;
                  scaleY = (oldMaxY - point.y) / oldHeight;
                  
                  // 等比例缩放
                  if (isShiftPressed) {
                    // 选择较大的缩放比例，保持宽高比
                    if (Math.abs(scaleX) > Math.abs(scaleY)) {
                      scaleY = scaleX;
                      // 重新调整点y坐标
                      point.y = oldMaxY - scaleY * oldHeight;
                    } else {
                      scaleX = scaleY;
                      // 重新调整点x坐标
                      point.x = oldMinX + scaleX * oldWidth;
                    }
                  }
                  
                  translateY = point.y - oldMinY;
                  break;
                case 'BOTTOM_LEFT':
                  // 计算从右上角到鼠标位置的宽高
                  const widthBL = oldMaxX - point.x;
                  const heightBL = point.y - oldMinY;
                  
                  // 应用最小尺寸限制
                  const limitedWidthBL = Math.max(widthBL, minWidth);
                  const limitedHeightBL = Math.max(heightBL, minHeight);
                  
                  // 如果达到最小限制，重新计算鼠标位置以避免推动效果
                  if (widthBL < minWidth) {
                    point.x = oldMaxX - minWidth;
                  }
                  if (heightBL < minHeight) {
                    point.y = oldMinY + minHeight;
                  }
                  
                  scaleX = (oldMaxX - point.x) / oldWidth;
                  scaleY = (point.y - oldMinY) / oldHeight;
                  
                  // 等比例缩放
                  if (isShiftPressed) {
                    // 选择较大的缩放比例，保持宽高比
                    if (Math.abs(scaleX) > Math.abs(scaleY)) {
                      scaleY = scaleX;
                      // 重新调整点y坐标
                      point.y = oldMinY + scaleY * oldHeight;
                    } else {
                      scaleX = scaleY;
                      // 重新调整点x坐标
                      point.x = oldMaxX - scaleX * oldWidth;
                    }
                  }
                  
                  translateX = point.x - oldMinX;
                  break;
                case 'BOTTOM_RIGHT':
                  // 计算从左上角到鼠标位置的宽高
                  const widthBR = point.x - oldMinX;
                  const heightBR = point.y - oldMinY;
                  
                  // 应用最小尺寸限制
                  const limitedWidthBR = Math.max(widthBR, minWidth);
                  const limitedHeightBR = Math.max(heightBR, minHeight);
                  
                  // 如果达到最小限制，重新计算鼠标位置以避免推动效果
                  if (widthBR < minWidth) {
                    point.x = oldMinX + minWidth;
                  }
                  if (heightBR < minHeight) {
                    point.y = oldMinY + minHeight;
                  }
                  
                  scaleX = (point.x - oldMinX) / oldWidth;
                  scaleY = (point.y - oldMinY) / oldHeight;
                  
                  // 等比例缩放
                  if (isShiftPressed) {
                    // 选择较大的缩放比例，保持宽高比
                    if (Math.abs(scaleX) > Math.abs(scaleY)) {
                      scaleY = scaleX;
                      // 重新调整点y坐标
                      point.y = oldMinY + scaleY * oldHeight;
                    } else {
                      scaleX = scaleY;
                      // 重新调整点x坐标
                      point.x = oldMinX + scaleX * oldWidth;
                    }
                  }
                  break;
                case 'TOP_CENTER':
                  // 应用最小高度限制
                  if (oldMaxY - point.y < minHeight) {
                    point.y = oldMaxY - minHeight;
                  }
                  
                  scaleY = (oldMaxY - point.y) / oldHeight;
                  
                  // 等比例缩放
                  if (isShiftPressed) {
                    scaleX = scaleY;
                    // 保持水平居中
                    translateX = -oldWidth * (scaleX - 1) / 2;
                  }
                  translateY = point.y - oldMinY;
                  break;
                case 'BOTTOM_CENTER':
                  // 应用最小高度限制
                  if (point.y - oldMinY < minHeight) {
                    point.y = oldMinY + minHeight;
                  }
                  
                  scaleY = (point.y - oldMinY) / oldHeight;
                  
                  // 等比例缩放
                  if (isShiftPressed) {
                    scaleX = scaleY;
                    // 保持水平居中
                    translateX = -oldWidth * (scaleX - 1) / 2;
                  }
                  break;
                case 'LEFT_CENTER':
                  // 应用最小宽度限制
                  if (oldMaxX - point.x < minWidth) {
                    point.x = oldMaxX - minWidth;
                  }
                  
                  scaleX = (oldMaxX - point.x) / oldWidth;
                  
                  // 等比例缩放
                  if (isShiftPressed) {
                    scaleY = scaleX;
                    // 保持垂直居中
                    translateY = -oldHeight * (scaleY - 1) / 2;
                  }
                  translateX = point.x - oldMinX;
                  break;
                case 'RIGHT_CENTER':
                  // 应用最小宽度限制
                  if (point.x - oldMinX < minWidth) {
                    point.x = oldMinX + minWidth;
                  }
                  
                  scaleX = (point.x - oldMinX) / oldWidth;
                  
                  // 等比例缩放
                  if (isShiftPressed) {
                    scaleY = scaleX;
                    // 保持垂直居中
                    translateY = -oldHeight * (scaleY - 1) / 2;
                  }
                  break;
              }
              
              // 确保缩放比例不会太小
              scaleX = Math.max(minScaleX, scaleX);
              scaleY = Math.max(minScaleY, scaleY);
              
              // 计算新的组合大小和位置
              let newMinX = oldMinX;
              let newMinY = oldMinY;
              let newMaxX = oldMaxX;
              let newMaxY = oldMaxY;
              
              // 根据控制点计算新的组合边界
              switch (activeControlPointPosition) {
                case 'TOP_LEFT':
                  newMinX = oldMaxX - oldWidth * scaleX;
                  newMinY = oldMaxY - oldHeight * scaleY;
                  newMaxX = oldMaxX;
                  newMaxY = oldMaxY;
                  break;
                case 'TOP_RIGHT':
                  newMinX = oldMinX;
                  newMinY = oldMaxY - oldHeight * scaleY;
                  newMaxX = oldMinX + oldWidth * scaleX;
                  newMaxY = oldMaxY;
                  break;
                case 'BOTTOM_LEFT':
                  newMinX = oldMaxX - oldWidth * scaleX;
                  newMinY = oldMinY;
                  newMaxX = oldMaxX;
                  newMaxY = oldMinY + oldHeight * scaleY;
                  break;
                case 'BOTTOM_RIGHT':
                  newMinX = oldMinX;
                  newMinY = oldMinY;
                  newMaxX = oldMinX + oldWidth * scaleX;
                  newMaxY = oldMinY + oldHeight * scaleY;
                  break;
                case 'TOP_CENTER':
                  newMinX = oldMinX + (oldWidth - oldWidth * scaleX) / 2;
                  newMinY = oldMaxY - oldHeight * scaleY;
                  newMaxX = newMinX + oldWidth * scaleX;
                  newMaxY = oldMaxY;
                  break;
                case 'BOTTOM_CENTER':
                  newMinX = oldMinX + (oldWidth - oldWidth * scaleX) / 2;
                  newMinY = oldMinY;
                  newMaxX = newMinX + oldWidth * scaleX;
                  newMaxY = oldMinY + oldHeight * scaleY;
                  break;
                case 'LEFT_CENTER':
                  newMinX = oldMaxX - oldWidth * scaleX;
                  newMinY = oldMinY + (oldHeight - oldHeight * scaleY) / 2;
                  newMaxX = oldMaxX;
                  newMaxY = newMinY + oldHeight * scaleY;
                  break;
                case 'RIGHT_CENTER':
                  newMinX = oldMinX;
                  newMinY = oldMinY + (oldHeight - oldHeight * scaleY) / 2;
                  newMaxX = oldMinX + oldWidth * scaleX;
                  newMaxY = newMinY + oldHeight * scaleY;
                  break;
              }
              
              // 应用变换到组合中的每个元素
              for (const element of groupElements) {
                if (element.type === 'line' && element.index < drawingService.getLines().length) {
                  const line = drawingService.getLines()[element.index];
                  if (line && line.points && line.points.length > 0) {
                    // 计算新的点位置
                    const newPoints = line.points.map(p => {
                      // 计算点在原始组合中的相对位置 (0-1范围)
                      const relativeX = (p.x - oldMinX) / oldWidth;
                      const relativeY = (p.y - oldMinY) / oldHeight;
                      
                      // 根据相对位置计算在新组合中的坐标
                      return {
                        x: newMinX + relativeX * (newMaxX - newMinX),
                        y: newMinY + relativeY * (newMaxY - newMinY)
                      };
                    });
                    
                    // 更新线条的点
                    drawingService.updateLinePoints(element.index, newPoints);
                  }
                } else if (element.type === 'text' && element.index < textService.getTexts().length) {
                  const text = textService.getTexts()[element.index];
                  if (text) {
                    // 计算文本在原始组合中的相对位置 (0-1范围)
                    const relativeX = ((text.boxX ?? 0) - oldMinX) / oldWidth;
                    const relativeY = ((text.boxY ?? 0) - oldMinY) / oldHeight;
                    const relativeWidth = (text.boxWidth ?? 0) / oldWidth;
                    const relativeHeight = (text.boxHeight ?? 0) / oldHeight;
                    
                    // 根据相对位置计算在新组合中的位置和大小
                    const newX = newMinX + relativeX * (newMaxX - newMinX);
                    const newY = newMinY + relativeY * (newMaxY - newMinY);
                    const newWidth = relativeWidth * (newMaxX - newMinX);
                    const newHeight = relativeHeight * (newMaxY - newMinY);
                    
                    // 更新文本位置和大小
                    text.boxX = newX;
                    text.boxY = newY;
                    text.boxWidth = newWidth;
                    text.boxHeight = newHeight;
                  }
                } else if (element.type === 'card' && element.index < cardService.getCards().length) {
                  const card = cardService.getCards()[element.index];
                  if (card) {
                    // 计算卡片在原始组合中的相对位置 (0-1范围)
                    const relativeX = (card.x - oldMinX) / oldWidth;
                    const relativeY = (card.y - oldMinY) / oldHeight;
                    const relativeWidth = card.width / oldWidth;
                    const relativeHeight = card.height / oldHeight;
                    
                    // 根据相对位置计算在新组合中的位置和大小
                    const newX = newMinX + relativeX * (newMaxX - newMinX);
                    const newY = newMinY + relativeY * (newMaxY - newMinY);
                    const newWidth = relativeWidth * (newMaxX - newMinX);
                    const newHeight = relativeHeight * (newMaxY - newMinY);
                    
                    // 更新卡片位置和大小
                    card.x = newX;
                    card.y = newY;
                    card.width = newWidth;
                    card.height = newHeight;
                  }
                } else if (element.type === 'image' && element.index < imageService.getImages().length) {
                  const image = imageService.getImages()[element.index];
                  if (image) {
                    // 计算图片在原始组合中的相对位置 (0-1范围)
                    const relativeX = (image.x - oldMinX) / oldWidth;
                    const relativeY = (image.y - oldMinY) / oldHeight;
                    const relativeWidth = image.width / oldWidth;
                    const relativeHeight = image.height / oldHeight;
                    
                    // 根据相对位置计算在新组合中的位置和大小
                    const newX = newMinX + relativeX * (newMaxX - newMinX);
                    const newY = newMinY + relativeY * (newMaxY - newMinY);
                    const newWidth = relativeWidth * (newMaxX - newMinX);
                    const newHeight = relativeHeight * (newMaxY - newMinY);
                    
                    // 更新图片位置和大小
                    image.x = newX;
                    image.y = newY;
                    image.width = newWidth;
                    image.height = newHeight;
                  }
                }
              }
              
              // 在缩放完成后清除SelectionService中缓存的选择边界
              // 这将确保下次渲染时重新计算组合边界框，而不是使用缓存的旧边界
              selectionService.clearSelectedBounds();
              
              // 触发重新渲染
              render();
            }
          }
        }
      }
      
      switch (canvasMode) {
        case CanvasMode.DRAW:
          if (isDrawing) {
            drawingService.continueDrawing(point);
          }
          break;
          
        case CanvasMode.SELECT:
          if (selectionService.isContentMoving()) {
            moveSelectedContent(point);
          } else if (selectionService.isSelecting()) {
            selectionService.updateSelection(point);
            // 添加实时预览即将被选中的对象
            selectionService.previewSelection(drawingService.getLines(), textService.getTexts(), cardService.getCards(), imageService.getImages());
          }
          break;
          
        case CanvasMode.SHAPE:
          if (isDrawing) {
            drawingService.updateShape(point);
          }
          break;
      }
      
      render();
    }
  }, [isDrawing, isRightDragging, isSpacePressed, drawingService, selectionService, viewportService, textService, cardService, imageService, render, moveSelectedContent, completeLineSelection, handleStopMoving, findClickedImage, isDraggingControlPoint, activeControlPointPosition, canvasMode, isFixed]);

  // 全局鼠标移动和抬起事件，用于处理鼠标超出画布范围的情况
  useEffect(() => {
    const handleGlobalMouseUp = (e: MouseEvent) => {
      if (isDraggingControlPoint) {
        setIsDraggingControlPoint(false);
        setActiveControlPointPosition(null);
        selectionService.setActiveControlPoint(null);
        
        // 触发工具操作完成事件，以便自动保存缩放后的元素
        document.dispatchEvent(new CustomEvent('tool-operation-complete'));
      }
      // 处理右键拖拽结束
      if (e.button === 2) {
        if (viewportService.isDraggingViewport()) {
          viewportService.stopDrag();
          setIsRightDragging(false);
        }
        
        // 右键释放时，如果在选择状态，清除选择
        if (selectionService.isSelecting()) {
          selectionService.clearSelection();
        }
        
        render();
        return;
      }
      
      // 处理其他鼠标按键释放或空格平移
      if (viewportService.isDraggingViewport()) {
        viewportService.stopDrag();
        if (isRightDragging) {
          setIsRightDragging(false);
        }
      }
      
      if (canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const point = viewportService.clientToCanvas(x, y);
        
        // 右键释放不处理绘制完成
        if (e.button === 2) {
          render();
          return;
        }
        
        switch (canvasMode) {
          case CanvasMode.DRAW:
            if (isDrawing) {
              drawingService.finishDrawing();
              setIsDrawing(false);
            }
            break;
            
          case CanvasMode.SELECT:
            if (selectionService.isContentMoving()) {
              selectionService.stopMoving();
              handleStopMoving();
            } else if (selectionService.isSelecting()) {
              // 使用selectionService的completeSelection方法
              selectionService.completeSelection(
                drawingService.getLines(), 
                e.shiftKey,
                textService.getTexts(),
                cardService.getCards(),
                imageService.getImages()
              );
            }
            break;
            
          case CanvasMode.SHAPE:
            if (isDrawing) {
              drawingService.finishShape();
              setIsDrawing(false);
            }
            break;
            
          case CanvasMode.CARD:
            // 卡片创建不需要在鼠标抬起时进行特殊处理
            // 编辑已经在CardService内部处理
            break;
        }
        
        render();
      }
      
      // 重新渲染以确保控制点状态更新
      render();
      
      // 在鼠标抬起时清除控制点激活状态，使控制点恢复为空心
      selectionService.setActiveControlPoint(null);
      setActiveControlPointPosition(null);
      setIsDraggingControlPoint(false);
    };
    
    // 添加全局事件监听器
    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('mouseup', handleGlobalMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [canvasMode, isDrawing, isRightDragging, isSpacePressed, drawingService, selectionService, viewportService, textService, cardService, imageService, render, moveSelectedContent, completeLineSelection, handleStopMoving, findClickedImage, isDraggingControlPoint, activeControlPointPosition, handleGlobalMouseMove]);

  // 处理单击事件
  const handleClick = useCallback((e: React.MouseEvent) => {
    // 步骤1: 模式检查
    // 只在选择模式下处理单击事件，其他模式下忽略
    if (canvasMode !== CanvasMode.SELECT) return;
    
    // 步骤2: 获取画布坐标点
    // 将屏幕坐标转换为画布坐标系中的点
    const point = getCanvasPoint(e.clientX, e.clientY);
    
    // 步骤3: 检查编辑状态
    // 确认是否已有文本正在编辑
    if (textService.getTexts().some((text: TextData) => text.isEditing)) {
      // 如果已有文本编辑中，则忽略本次单击事件
      // 防止多个文本同时编辑
      return;
    }
    
    // 步骤4: 检查是否正在移动或发生了拖动
    // 修改: 使用与右键菜单相同的距离阈值标准
    const distanceThreshold = 5; // 与handleContextMenu中保持一致
    if (selectionService.isContentMoving() || (mouseDownPoint && 
        (Math.abs(point.x - mouseDownPoint.x) > distanceThreshold || 
         Math.abs(point.y - mouseDownPoint.y) > distanceThreshold))) {
      return;
    }
    
    // 步骤5: 检测点击位置是否在现有卡片上
    const clickedCardIndex = findClickedCard(point);
    if (clickedCardIndex !== -1) {
      // 开始编辑卡片
      cardService.startEditing(clickedCardIndex);
      return;
    }
    
    e.stopPropagation();
  }, [canvasMode, getCanvasPoint, textService, cardService, findClickedCard, selectionService, mouseDownPoint]);

  // 是否需要暂停键盘快捷键（编辑文本时）
  const shouldSuspendShortcuts = () => {
    return textService.getTexts().some((text: TextData) => text.isEditing);
  };

  // 判断内容是否超出视图
  const checkContentInView = useCallback(() => {
    // 获取所有内容的边界
    const lines = drawingService.getLines();
    const texts = textService.getTexts();
    const cards = cardService.getCards();
    const images = imageService.getImages();
    
    // 如果没有内容，则不需要显示按钮
    if (lines.length === 0 && texts.length === 0 && cards.length === 0 && images.length === 0) {
      setIsContentOutOfView(false);
      return;
    }
    
    // 计算所有内容的边界
    let contentBounds = {
      minX: Number.MAX_VALUE,
      minY: Number.MAX_VALUE,
      maxX: -Number.MAX_VALUE,
      maxY: -Number.MAX_VALUE
    };
    
    // 计算线条边界 - 使用SelectionService中的方法获取线条的边界框
    lines.forEach(line => {
      // 计算线条周围的边界框
      const lineBounds = {
        minX: Number.MAX_VALUE,
        minY: Number.MAX_VALUE,
        maxX: -Number.MAX_VALUE,
        maxY: -Number.MAX_VALUE
      };
      
      // 计算所有点的边界
      line.points.forEach(point => {
        lineBounds.minX = Math.min(lineBounds.minX, point.x);
        lineBounds.minY = Math.min(lineBounds.minY, point.y);
        lineBounds.maxX = Math.max(lineBounds.maxX, point.x);
        lineBounds.maxY = Math.max(lineBounds.maxY, point.y);
      });
      
      // 考虑线条粗细
      const lineThickness = line.size || 1;
      lineBounds.minX -= lineThickness;
      lineBounds.minY -= lineThickness;
      lineBounds.maxX += lineThickness;
      lineBounds.maxY += lineThickness;
      
      // 更新整体内容边界
      contentBounds.minX = Math.min(contentBounds.minX, lineBounds.minX);
      contentBounds.minY = Math.min(contentBounds.minY, lineBounds.minY);
      contentBounds.maxX = Math.max(contentBounds.maxX, lineBounds.maxX);
      contentBounds.maxY = Math.max(contentBounds.maxY, lineBounds.maxY);
    });
    
    // 计算文本边界 - 使用一致的边界框估算
    texts.forEach(text => {
      // 简化版的文本边界计算
      const textBounds = {
        minX: text.baseX - 50, // 估算文本宽度
        minY: text.baseY - 10,
        maxX: text.baseX + 50,
        maxY: text.baseY + 30
      };
      
      contentBounds.minX = Math.min(contentBounds.minX, textBounds.minX);
      contentBounds.minY = Math.min(contentBounds.minY, textBounds.minY);
      contentBounds.maxX = Math.max(contentBounds.maxX, textBounds.maxX);
      contentBounds.maxY = Math.max(contentBounds.maxY, textBounds.maxY);
    });
    
    // 计算卡片边界 - 使用卡片的边界框
    cards.forEach(card => {
      const cardBounds = {
        minX: card.x,
        minY: card.y,
        maxX: card.x + card.width,
        maxY: card.y + card.height
      };
      
      contentBounds.minX = Math.min(contentBounds.minX, cardBounds.minX);
      contentBounds.minY = Math.min(contentBounds.minY, cardBounds.minY);
      contentBounds.maxX = Math.max(contentBounds.maxX, cardBounds.maxX);
      contentBounds.maxY = Math.max(contentBounds.maxY, cardBounds.maxY);
    });
    
    // 计算图片边界 - 使用图片的边界框
    images.forEach(image => {
      if (image.rotation && image.rotation !== 0) {
        // 对于旋转的图片，计算旋转后的边界框
        // 计算图片中心点
        const centerX = image.x + image.width / 2;
        const centerY = image.y + image.height / 2;
        
        // 获取图片的四个角点（相对于中心点）
        const corners = [
          { x: -image.width / 2, y: -image.height / 2 },
          { x: image.width / 2, y: -image.height / 2 },
          { x: image.width / 2, y: image.height / 2 },
          { x: -image.width / 2, y: image.height / 2 }
        ];
        
        // 旋转角点并计算边界
        const rotation = image.rotation;
        const cos = Math.cos(rotation);
        const sin = Math.sin(rotation);
        
        // 初始化边界
        const imageBounds = {
          minX: Number.MAX_VALUE,
          minY: Number.MAX_VALUE,
          maxX: -Number.MAX_VALUE,
          maxY: -Number.MAX_VALUE
        };
        
        // 计算旋转后的边界
        corners.forEach(corner => {
          // 旋转角点
          const rotatedX = corner.x * cos - corner.y * sin;
          const rotatedY = corner.x * sin + corner.y * cos;
          
          // 将角点转换回世界坐标
          const worldX = rotatedX + centerX;
          const worldY = rotatedY + centerY;
          
          // 更新图片边界
          imageBounds.minX = Math.min(imageBounds.minX, worldX);
          imageBounds.minY = Math.min(imageBounds.minY, worldY);
          imageBounds.maxX = Math.max(imageBounds.maxX, worldX);
          imageBounds.maxY = Math.max(imageBounds.maxY, worldY);
        });
        
        // 更新整体内容边界
        contentBounds.minX = Math.min(contentBounds.minX, imageBounds.minX);
        contentBounds.minY = Math.min(contentBounds.minY, imageBounds.minY);
        contentBounds.maxX = Math.max(contentBounds.maxX, imageBounds.maxX);
        contentBounds.maxY = Math.max(contentBounds.maxY, imageBounds.maxY);
      } else {
        // 无旋转的图片直接使用矩形边界
        const imageBounds = {
          minX: image.x,
          minY: image.y,
          maxX: image.x + image.width,
          maxY: image.y + image.height
        };
        
        contentBounds.minX = Math.min(contentBounds.minX, imageBounds.minX);
        contentBounds.minY = Math.min(contentBounds.minY, imageBounds.minY);
        contentBounds.maxX = Math.max(contentBounds.maxX, imageBounds.maxX);
        contentBounds.maxY = Math.max(contentBounds.maxY, imageBounds.maxY);
      }
    });
    
    // 获取当前可视区域的边界
    const canvasWidth = viewportService.getCanvasSize().width;
    const canvasHeight = viewportService.getCanvasSize().height;
    const scale = viewportService.getScale();
    const offsetX = viewportService.getOffsetX();
    const offsetY = viewportService.getOffsetY();
    
    // 计算可视区域在画布坐标系中的边界
    const viewBounds = {
      minX: -offsetX,
      minY: -offsetY,
      maxX: canvasWidth / scale - offsetX,
      maxY: canvasHeight / scale - offsetY
    };
    
    // 基础屏幕距离阈值（单位：像素）- 当内容边界距离屏幕边缘超过这么多像素时显示按钮
    // 对于缩放比例为1的情况，这个值表示内容需要离开屏幕边缘的像素距离
    const BASE_SCREEN_THRESHOLD = 500;
    
    // 将屏幕阈值转换为画布坐标系中的阈值（考虑缩放）
    const threshold = BASE_SCREEN_THRESHOLD / scale;
    
    // 检查内容与可视区域的位置关系
    // 当内容完全在可视区域外，或者距离可视区域边缘过远时才显示按钮
    const isTooFar = (
      // 内容完全在可视区域左侧，且距离足够远
      (contentBounds.maxX < viewBounds.minX && Math.abs(contentBounds.maxX - viewBounds.minX) > threshold) ||
      // 内容完全在可视区域右侧，且距离足够远
      (contentBounds.minX > viewBounds.maxX && Math.abs(contentBounds.minX - viewBounds.maxX) > threshold) ||
      // 内容完全在可视区域上方，且距离足够远
      (contentBounds.maxY < viewBounds.minY && Math.abs(contentBounds.maxY - viewBounds.minY) > threshold) ||
      // 内容完全在可视区域下方，且距离足够远
      (contentBounds.minY > viewBounds.maxY && Math.abs(contentBounds.minY - viewBounds.maxY) > threshold)
    );
    
    setIsContentOutOfView(isTooFar);
  }, [drawingService, textService, cardService, imageService, viewportService]);
  
  // 添加检查缩放是否接近极限的函数
  const checkIfNearZoomLimit = useCallback(() => {
    const currentScale = viewportService.getScale();
    // 缩放范围是0.1到10.0，根据是否接近极限值的90%来判断
    const isNearMin = currentScale <= 0.1 * 1.1; // 接近最小值的110%
    const isNearMax = currentScale >= 10.0 * 0.9; // 接近最大值的90%
    setIsNearZoomLimit(isNearMin || isNearMax);
  }, [viewportService]);

  // 在适当的位置添加对checkIfNearZoomLimit的调用
  useEffect(() => {
    // 监听viewport-changed事件
    const handleViewportChanged = () => {
      checkContentInView();
      checkIfNearZoomLimit();
    };
    
    document.addEventListener('viewport-changed', handleViewportChanged);
    
    return () => {
      document.removeEventListener('viewport-changed', handleViewportChanged);
    };
  }, [checkContentInView, checkIfNearZoomLimit]);
  
  // 监听视口变化
  useEffect(() => {
    const handleViewportChanged = () => {
      checkContentInView();
    };
    
    document.addEventListener('viewport-changed', handleViewportChanged);
    
    // 初始检查
    checkContentInView();
    
    return () => {
      document.removeEventListener('viewport-changed', handleViewportChanged);
    };
  }, [checkContentInView]);
  
  // 处理回到内容区域的点击
  const handleGoToContent = useCallback(() => {
    // 获取所有内容的边界
    const lines = drawingService.getLines();
    const texts = textService.getTexts();
    const cards = cardService.getCards();
    const images = imageService.getImages();
    
    // 计算所有内容的边界
    let contentBounds = {
      minX: Number.MAX_VALUE,
      minY: Number.MAX_VALUE,
      maxX: -Number.MAX_VALUE,
      maxY: -Number.MAX_VALUE
    };
    
    // 计算线条边界 - 使用SelectionService中的方法获取线条的边界框
    lines.forEach(line => {
      // 计算线条周围的边界框
      const lineBounds = {
        minX: Number.MAX_VALUE,
        minY: Number.MAX_VALUE,
        maxX: -Number.MAX_VALUE,
        maxY: -Number.MAX_VALUE
      };
      
      // 计算所有点的边界
      line.points.forEach(point => {
        lineBounds.minX = Math.min(lineBounds.minX, point.x);
        lineBounds.minY = Math.min(lineBounds.minY, point.y);
        lineBounds.maxX = Math.max(lineBounds.maxX, point.x);
        lineBounds.maxY = Math.max(lineBounds.maxY, point.y);
      });
      
      // 考虑线条粗细
      const lineThickness = line.size || 1;
      lineBounds.minX -= lineThickness;
      lineBounds.minY -= lineThickness;
      lineBounds.maxX += lineThickness;
      lineBounds.maxY += lineThickness;
      
      // 更新整体内容边界
      contentBounds.minX = Math.min(contentBounds.minX, lineBounds.minX);
      contentBounds.minY = Math.min(contentBounds.minY, lineBounds.minY);
      contentBounds.maxX = Math.max(contentBounds.maxX, lineBounds.maxX);
      contentBounds.maxY = Math.max(contentBounds.maxY, lineBounds.maxY);
    });
    
    // 计算文本边界 - 使用一致的边界框估算
    texts.forEach(text => {
      const textBounds = {
        minX: text.baseX - 50, // 估算文本宽度
        minY: text.baseY - 10,
        maxX: text.baseX + 50,
        maxY: text.baseY + 30
      };
      
      contentBounds.minX = Math.min(contentBounds.minX, textBounds.minX);
      contentBounds.minY = Math.min(contentBounds.minY, textBounds.minY);
      contentBounds.maxX = Math.max(contentBounds.maxX, textBounds.maxX);
      contentBounds.maxY = Math.max(contentBounds.maxY, textBounds.maxY);
    });
    
    // 计算卡片边界 - 使用卡片的边界框
    cards.forEach(card => {
      const cardBounds = {
        minX: card.x,
        minY: card.y,
        maxX: card.x + card.width,
        maxY: card.y + card.height
      };
      
      contentBounds.minX = Math.min(contentBounds.minX, cardBounds.minX);
      contentBounds.minY = Math.min(contentBounds.minY, cardBounds.minY);
      contentBounds.maxX = Math.max(contentBounds.maxX, cardBounds.maxX);
      contentBounds.maxY = Math.max(contentBounds.maxY, cardBounds.maxY);
    });
    
    // 计算图片边界 - 使用图片的边界框
    images.forEach(image => {
      if (image.rotation && image.rotation !== 0) {
        // 对于旋转的图片，计算旋转后的边界框
        // 计算图片中心点
        const centerX = image.x + image.width / 2;
        const centerY = image.y + image.height / 2;
        
        // 获取图片的四个角点（相对于中心点）
        const corners = [
          { x: -image.width / 2, y: -image.height / 2 },
          { x: image.width / 2, y: -image.height / 2 },
          { x: image.width / 2, y: image.height / 2 },
          { x: -image.width / 2, y: image.height / 2 }
        ];
        
        // 旋转角点并计算边界
        const rotation = image.rotation;
        const cos = Math.cos(rotation);
        const sin = Math.sin(rotation);
        
        // 初始化边界
        const imageBounds = {
          minX: Number.MAX_VALUE,
          minY: Number.MAX_VALUE,
          maxX: -Number.MAX_VALUE,
          maxY: -Number.MAX_VALUE
        };
        
        // 计算旋转后的边界
        corners.forEach(corner => {
          // 旋转角点
          const rotatedX = corner.x * cos - corner.y * sin;
          const rotatedY = corner.x * sin + corner.y * cos;
          
          // 将角点转换回世界坐标
          const worldX = rotatedX + centerX;
          const worldY = rotatedY + centerY;
          
          // 更新图片边界
          imageBounds.minX = Math.min(imageBounds.minX, worldX);
          imageBounds.minY = Math.min(imageBounds.minY, worldY);
          imageBounds.maxX = Math.max(imageBounds.maxX, worldX);
          imageBounds.maxY = Math.max(imageBounds.maxY, worldY);
        });
        
        // 更新整体内容边界
        contentBounds.minX = Math.min(contentBounds.minX, imageBounds.minX);
        contentBounds.minY = Math.min(contentBounds.minY, imageBounds.minY);
        contentBounds.maxX = Math.max(contentBounds.maxX, imageBounds.maxX);
        contentBounds.maxY = Math.max(contentBounds.maxY, imageBounds.maxY);
      } else {
        // 无旋转的图片直接使用矩形边界
        const imageBounds = {
          minX: image.x,
          minY: image.y,
          maxX: image.x + image.width,
          maxY: image.y + image.height
        };
        
        contentBounds.minX = Math.min(contentBounds.minX, imageBounds.minX);
        contentBounds.minY = Math.min(contentBounds.minY, imageBounds.minY);
        contentBounds.maxX = Math.max(contentBounds.maxX, imageBounds.maxX);
        contentBounds.maxY = Math.max(contentBounds.maxY, imageBounds.maxY);
      }
    });
    
    // 检查边界是否有效
    if (contentBounds.minX === Number.MAX_VALUE || 
        contentBounds.maxX === -Number.MAX_VALUE || 
        contentBounds.minY === Number.MAX_VALUE || 
        contentBounds.maxY === -Number.MAX_VALUE) {
      // 边界无效，可能没有内容，直接返回到原点
      viewportService.startSmoothScroll(0, 0);
      return;
    }
    
    // 计算内容的中心点
    const contentCenterX = (contentBounds.minX + contentBounds.maxX) / 2;
    const contentCenterY = (contentBounds.minY + contentBounds.maxY) / 2;
    
    // 计算画布中心点（相对于左上角的偏移）
    const canvasSize = viewportService.getCanvasSize();
    const scale = viewportService.getScale();
    const canvasCenterX = canvasSize.width / (2 * scale);
    const canvasCenterY = canvasSize.height / (2 * scale);
    
    // 计算需要的偏移量，使内容中心与画布中心对齐
    const targetOffsetX = canvasCenterX - contentCenterX;
    const targetOffsetY = canvasCenterY - contentCenterY;
    
    // 使用平滑滚动到目标位置
    viewportService.startSmoothScroll(targetOffsetX, targetOffsetY);
    
    // 触发工具操作完成事件，自动保存当前内容状态
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('tool-operation-complete'));
    }, 500);
  }, [drawingService, textService, cardService, imageService, viewportService]);

  // 添加重置缩放的处理函数
  const handleResetZoom = useCallback(() => {
    // 获取画布中心点
    const canvasWidth = viewportService.getCanvasSize().width;
    const canvasHeight = viewportService.getCanvasSize().height;
    const centerX = canvasWidth / 2;
    const centerY = canvasHeight / 2;
    
    // 获取当前缩放值和偏移量
    const currentScale = viewportService.getScale();
    const currentOffsetX = viewportService.getOffsetX();
    const currentOffsetY = viewportService.getOffsetY();
    
    // 计算屏幕中心对应的画布坐标（这个点在缩放过程中应该保持在屏幕中心）
    const viewportCenterCanvasX = (centerX / currentScale) - currentOffsetX;
    const viewportCenterCanvasY = (centerY / currentScale) - currentOffsetY;
    
    // 目标缩放值
    const targetScale = 1.0;
    
    // 使用平滑动画定义
    let startTime = Date.now();
    const duration = 300; // 设置过渡动画时间为300毫秒
    const initialScale = currentScale;
    
    // 动画函数
    const animateReset = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // 使用缓动函数使动画更自然
      const easedProgress = progress * (2 - progress); // easeOutQuad
      
      // 计算当前的缩放值
      const scale = initialScale + (targetScale - initialScale) * easedProgress;
      
      // 重要：在每一帧中，根据新的缩放值动态计算偏移量
      // 这确保了屏幕中心始终对应同一个画布坐标点
      const offsetX = -(viewportCenterCanvasX - (centerX / scale));
      const offsetY = -(viewportCenterCanvasY - (centerY / scale));
      
      // 更新视口状态
      viewportService.setViewportState(scale, offsetX, offsetY);
      
      // 如果动画未结束，则继续下一帧
      if (progress < 1) {
        requestAnimationFrame(animateReset);
      } else {
        // 动画结束，触发保存事件
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('tool-operation-complete'));
        }, 200);
      }
    };
    
    // 开始动画
    animateReset();
  }, [viewportService]);

  // 显示状态消息的辅助函数
  const showMessage = useCallback((message: string) => {
    // 清除之前的定时器
    if (statusMessageTimerRef.current) {
      clearTimeout(statusMessageTimerRef.current);
    }
    
    // 直接设置新消息，不隐藏组件
    setStatusMessage(message);
    setShowStatusMessage(true);
    
    // 2秒后自动隐藏
    statusMessageTimerRef.current = setTimeout(() => {
      setShowStatusMessage(false);
    }, 2000);
  }, []);

  // 添加一个鼠标单击事件处理函数，专门用于处理控制点的激活
  const handleControlPointClick = useCallback((e: React.MouseEvent) => {
    if (!canvasRef.current || !selectionService) return;
    
    const point = getCanvasPoint(e.clientX, e.clientY);
    
    // 获取当前选中元素信息
    const selectedIndices = selectionService.getSelectedIndices();
    const selectedTextIndices = selectionService.getSelectedTextIndices();
    const selectedCardIndices = selectionService.getSelectedCardIndices();
    const selectedImageIndices = selectionService.getSelectedImageIndices();
    
    // 检查所有选中的文本元素
    for (const textIndex of selectedTextIndices) {
      const text = textService.getTexts()[textIndex];
      if (!text) continue;
      
      const bounds = {
        minX: text.boxX ?? 0,
        minY: text.boxY ?? 0,
        maxX: (text.boxX ?? 0) + (text.boxWidth ?? 0),
        maxY: (text.boxY ?? 0) + (text.boxHeight ?? 0)
      };
      
      const controlPoint = selectionService.isPointInControlPoint(point, bounds);
      if (controlPoint !== null) {
        selectionService.setActiveControlPoint(controlPoint);
        setActiveControlPointPosition(controlPoint);
        render();
        return;
      }
    }
    
    // 检查所有选中的卡片元素
    for (const cardIndex of selectedCardIndices) {
      const card = cardService.getCards()[cardIndex];
      if (!card) continue;
      
      const bounds = {
        minX: card.x,
        minY: card.y,
        maxX: card.x + card.width,
        maxY: card.y + card.height
      };
      
      const controlPoint = selectionService.isPointInControlPoint(point, bounds);
      if (controlPoint !== null) {
        selectionService.setActiveControlPoint(controlPoint);
        setActiveControlPointPosition(controlPoint);
        render();
        return;
      }
    }
    
    // 检查所有选中的图片元素
    for (const imageIndex of selectedImageIndices) {
      const image = imageService.getImages()[imageIndex];
      if (!image) continue;
      
      const bounds = {
        minX: image.x,
        minY: image.y,
        maxX: image.x + image.width,
        maxY: image.y + image.height
      };
      
      const controlPoint = selectionService.isPointInControlPoint(point, bounds);
      if (controlPoint !== null) {
        selectionService.setActiveControlPoint(controlPoint);
        setActiveControlPointPosition(controlPoint);
        render();
        return;
      }
    }
    
    // 检查所有选中的线条元素
    for (const lineIndex of selectedIndices) {
      const line = drawingService.getLines()[lineIndex];
      if (!line) continue;
      
      const bounds = selectionService.getLineBoundingBox(line);
      
      const controlPoint = selectionService.isPointInControlPoint(point, bounds);
      if (controlPoint !== null) {
        selectionService.setActiveControlPoint(controlPoint);
        setActiveControlPointPosition(controlPoint);
        render();
        return;
      }
    }
  }, [selectionService, textService, cardService, imageService, drawingService, getCanvasPoint, render]);

  // 鼠标移动事件 - 仅用于canvas内的移动检测
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (canvasRef.current) {
      const point = getCanvasPoint(e.clientX, e.clientY);
      
      // 如果当前有文本正在编辑或者正在移动内容，不显示提示框
      if (textService.getTexts().some((text: TextData) => text.isEditing) || 
          selectionService.isContentMoving()) {
        setHoveredTextIndex(-1);
        return;
      }
      
      // 获取当前选中元素信息
      const selectedIndices = selectionService.getSelectedIndices();
      const selectedTextIndices = selectionService.getSelectedTextIndices();
      const selectedCardIndices = selectionService.getSelectedCardIndices();
      const selectedImageIndices = selectionService.getSelectedImageIndices();
      
      // 首先检查是否有选中元素，并尝试检测鼠标是否在控制点上
      let foundControlPoint = false;
      
      // 获取所有选中的组合ID
      const selectedGroupIds = selectionService.getSelectedGroupIds();
      
      // 检查文本元素的控制点
      for (const textIndex of selectedTextIndices) {
        const text = textService.getTexts()[textIndex];
        if (!text) continue;
        
        // 检查该文本是否属于任何选中的组合
        let isInSelectedGroup = false;
        if (groupService) {
          const groupId = groupService.isElementInAnyGroup('text', textIndex);
          isInSelectedGroup = groupId !== '' && selectedGroupIds.includes(groupId);
        }
        
        // 如果元素属于被选中的组合，则跳过对该元素控制点的检测
        if (isInSelectedGroup) continue;
        
        const bounds = {
          minX: text.boxX ?? 0,
          minY: text.boxY ?? 0,
          maxX: (text.boxX ?? 0) + (text.boxWidth ?? 0),
          maxY: (text.boxY ?? 0) + (text.boxHeight ?? 0)
        };
        
        const controlPoint = selectionService.isPointInControlPoint(point, bounds);
        if (controlPoint !== null) {
          // 仅更改鼠标样式，但不设置激活状态
          canvasRef.current.style.cursor = 'pointer';
          foundControlPoint = true;
          break;
        }
      }
      
      // 检查卡片元素的控制点
      if (!foundControlPoint) {
        for (const cardIndex of selectedCardIndices) {
          const card = cardService.getCards()[cardIndex];
          if (!card) continue;
          
          // 检查该卡片是否属于任何选中的组合
          let isInSelectedGroup = false;
          if (groupService) {
            const groupId = groupService.isElementInAnyGroup('card', cardIndex);
            isInSelectedGroup = groupId !== '' && selectedGroupIds.includes(groupId);
          }
          
          // 如果元素属于被选中的组合，则跳过对该元素控制点的检测
          if (isInSelectedGroup) continue;
          
          const bounds = {
            minX: card.x,
            minY: card.y,
            maxX: card.x + card.width,
            maxY: card.y + card.height
          };
          
          const controlPoint = selectionService.isPointInControlPoint(point, bounds);
          if (controlPoint !== null) {
            // 仅更改鼠标样式，但不设置激活状态
            canvasRef.current.style.cursor = 'pointer';
            foundControlPoint = true;
            break;
          }
        }
      }
      
      // 检查图片元素的控制点
      if (!foundControlPoint) {
        for (const imageIndex of selectedImageIndices) {
          const image = imageService.getImages()[imageIndex];
          if (!image) continue;
          
          // 检查该图片是否属于任何选中的组合
          let isInSelectedGroup = false;
          if (groupService) {
            const groupId = groupService.isElementInAnyGroup('image', imageIndex);
            isInSelectedGroup = groupId !== '' && selectedGroupIds.includes(groupId);
          }
          
          // 如果元素属于被选中的组合，则跳过对该元素控制点的检测
          if (isInSelectedGroup) continue;
          
          // 使用图片的精确边界，无边距
          const bounds = {
            minX: image.x,
            minY: image.y,
            maxX: image.x + image.width,
            maxY: image.y + image.height
          };
          
          const controlPoint = selectionService.isPointInControlPoint(point, bounds);
          if (controlPoint !== null) {
            // 根据控制点位置设置不同的鼠标样式
            switch (controlPoint) {
              case 'TOP_LEFT':
              case 'BOTTOM_RIGHT':
                canvasRef.current.style.cursor = 'nwse-resize';
                break;
              case 'TOP_RIGHT':
              case 'BOTTOM_LEFT':
                canvasRef.current.style.cursor = 'nesw-resize';
                break;
              case 'TOP_CENTER':
              case 'BOTTOM_CENTER':
                canvasRef.current.style.cursor = 'ns-resize';
                break;
              case 'LEFT_CENTER':
              case 'RIGHT_CENTER':
                canvasRef.current.style.cursor = 'ew-resize';
                break;
              default:
                canvasRef.current.style.cursor = 'pointer';
                break;
            }
            foundControlPoint = true;
            break;
          }
        }
      }
      
      // 检查线条元素的控制点
      if (!foundControlPoint) {
        for (const lineIndex of selectedIndices) {
          const line = drawingService.getLines()[lineIndex];
          if (!line || !line.points || line.points.length === 0) continue;
          
          // 检查该线条是否属于任何选中的组合
          let isInSelectedGroup = false;
          if (groupService) {
            const groupId = groupService.isElementInAnyGroup('line', lineIndex);
            isInSelectedGroup = groupId !== '' && selectedGroupIds.includes(groupId);
          }
          
          // 如果元素属于被选中的组合，则跳过对该元素控制点的检测
          if (isInSelectedGroup) continue;
          
          // 获取线条边界
          const lineBounds = selectionService.getLineBoundingBox(line);
          
          const controlPoint = selectionService.isPointInControlPoint(point, lineBounds);
          if (controlPoint !== null) {
            // 根据控制点位置设置不同的鼠标样式，与图片控制点保持一致
            switch (controlPoint) {
              case 'TOP_LEFT':
              case 'BOTTOM_RIGHT':
                canvasRef.current.style.cursor = 'nwse-resize';
                break;
              case 'TOP_RIGHT':
              case 'BOTTOM_LEFT':
                canvasRef.current.style.cursor = 'nesw-resize';
                break;
              case 'TOP_CENTER':
              case 'BOTTOM_CENTER':
                canvasRef.current.style.cursor = 'ns-resize';
                break;
              case 'LEFT_CENTER':
              case 'RIGHT_CENTER':
                canvasRef.current.style.cursor = 'ew-resize';
                break;
              default:
                canvasRef.current.style.cursor = 'pointer';
                break;
            }
            foundControlPoint = true;
            break;
          }
        }
      }
      
      // 检查组合框的控制点
      if (!foundControlPoint) {
        if (selectedGroupIds.length > 0) {
          for (const groupId of selectedGroupIds) {
            if (!groupService) continue;
            
            // 获取组合中的元素
            const groupElements = groupService.getGroupElements(groupId);
            
            // 初始化组合边界框
            let groupBounds: Bounds = {
              minX: Number.MAX_VALUE,
              minY: Number.MAX_VALUE,
              maxX: -Number.MAX_VALUE,
              maxY: -Number.MAX_VALUE
            };
            
            // 计算组合边界框
            for (const element of groupElements) {
              if (element.type === 'line' && element.index < drawingService.getLines().length) {
                const line = drawingService.getLines()[element.index];
                const lineBounds = selectionService.getLineBoundingBox(line, false);
                groupBounds.minX = Math.min(groupBounds.minX, lineBounds.minX);
                groupBounds.minY = Math.min(groupBounds.minY, lineBounds.minY);
                groupBounds.maxX = Math.max(groupBounds.maxX, lineBounds.maxX);
                groupBounds.maxY = Math.max(groupBounds.maxY, lineBounds.maxY);
              } else if (element.type === 'text' && element.index < textService.getTexts().length) {
                const text = textService.getTexts()[element.index];
                // 创建文本边界
                const textBounds = {
                  minX: text.boxX ?? 0,
                  minY: text.boxY ?? 0,
                  maxX: (text.boxX ?? 0) + (text.boxWidth ?? 0),
                  maxY: (text.boxY ?? 0) + (text.boxHeight ?? 0)
                };
                groupBounds.minX = Math.min(groupBounds.minX, textBounds.minX);
                groupBounds.minY = Math.min(groupBounds.minY, textBounds.minY);
                groupBounds.maxX = Math.max(groupBounds.maxX, textBounds.maxX);
                groupBounds.maxY = Math.max(groupBounds.maxY, textBounds.maxY);
              } else if (element.type === 'card' && element.index < cardService.getCards().length) {
                const card = cardService.getCards()[element.index];
                const cardBounds = {
                  minX: card.x,
                  minY: card.y,
                  maxX: card.x + card.width,
                  maxY: card.y + card.height
                };
                groupBounds.minX = Math.min(groupBounds.minX, cardBounds.minX);
                groupBounds.minY = Math.min(groupBounds.minY, cardBounds.minY);
                groupBounds.maxX = Math.max(groupBounds.maxX, cardBounds.maxX);
                groupBounds.maxY = Math.max(groupBounds.maxY, cardBounds.maxY);
              } else if (element.type === 'image' && element.index < imageService.getImages().length) {
                const image = imageService.getImages()[element.index];
                const imageBounds = {
                  minX: image.x,
                  minY: image.y,
                  maxX: image.x + image.width,
                  maxY: image.y + image.height
                };
                groupBounds.minX = Math.min(groupBounds.minX, imageBounds.minX);
                groupBounds.minY = Math.min(groupBounds.minY, imageBounds.minY);
                groupBounds.maxX = Math.max(groupBounds.maxX, imageBounds.maxX);
                groupBounds.maxY = Math.max(groupBounds.maxY, imageBounds.maxY);
              }
            }
            
            // 添加边距
            const currentScale = viewportService.getScale();
            const padding = 5 / currentScale;
            const groupBoundsWithPadding = {
              minX: groupBounds.minX - padding,
              minY: groupBounds.minY - padding,
              maxX: groupBounds.maxX + padding,
              maxY: groupBounds.maxY + padding
            };
            
            // 检查是否鼠标在组合框控制点上
            const controlPoint = selectionService.isPointInControlPoint(point, groupBoundsWithPadding);
            if (controlPoint !== null) {
              // 根据控制点位置设置不同的鼠标样式
              switch (controlPoint) {
                case 'TOP_LEFT':
                case 'BOTTOM_RIGHT':
                  canvasRef.current.style.cursor = 'nwse-resize';
                  break;
                case 'TOP_RIGHT':
                case 'BOTTOM_LEFT':
                  canvasRef.current.style.cursor = 'nesw-resize';
                  break;
                case 'TOP_CENTER':
                case 'BOTTOM_CENTER':
                  canvasRef.current.style.cursor = 'ns-resize';
                  break;
                case 'LEFT_CENTER':
                case 'RIGHT_CENTER':
                  canvasRef.current.style.cursor = 'ew-resize';
                  break;
                default:
                  canvasRef.current.style.cursor = 'pointer';
                  break;
              }
              foundControlPoint = true;
              break;
            }
          }
        }
      }
      
      // 如果不在控制点上，继续原来的逻辑
      if (!foundControlPoint) {
        // 检查鼠标是否在任何文本上
        const textIndex = findClickedText(point);
        
        if (textIndex !== -1) {
          // 确保此文本不在编辑状态
          const texts = textService.getTexts();
          if (texts[textIndex].isEditing) {
            setHoveredTextIndex(-1);
            return;
          }
          
          // 只有当文本有注释时才显示提示框
          if (texts[textIndex].hasAnnotation && texts[textIndex].annotation) {
            // 如果鼠标在文本上且文本有注释，显示提示框
            setHoveredTextIndex(textIndex);
            // 获取提示框位置（使用鼠标位置）
            setTooltipPosition({
              x: e.clientX,
              y: e.clientY - 50 // 在鼠标上方显示，距离调整为50px
            });
          } else {
            // 如果文本没有注释，不显示提示框
            setHoveredTextIndex(-1);
          }
          
          // 如果是选择工具模式，将鼠标设为文本选择样式
          if (canvasMode === CanvasMode.SELECT && canvasRef.current) {
            canvasRef.current.style.cursor = 'text';
          }
        } else {
          // 如果鼠标不在任何文本上，隐藏提示框
          setHoveredTextIndex(-1);
          
          // 恢复鼠标指针样式（只在SELECT模式下）
          if (canvasMode === CanvasMode.SELECT && canvasRef.current && !selectionService.isContentMoving()) {
            canvasRef.current.style.cursor = 'default';
          }
        }
      }
    }
  }, [getCanvasPoint, textService, canvasMode, selectionService, cardService, imageService, drawingService, findClickedText]);

  // 鼠标抬起事件 - 仅用于canvas内的释放检测
  const handleMouseUp = useCallback((e: React.MouseEvent) => {
    // 绝大部分处理逻辑已移至全局事件处理器中
  }, []);

  // 处理双击事件
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    // 步骤1: 模式检查
    // 只在选择模式下处理双击事件，其他模式下忽略
    if (canvasMode !== CanvasMode.SELECT) return;
    
    // 步骤2: 获取画布坐标点
    // 将屏幕坐标转换为画布坐标系中的点
    const point = getCanvasPoint(e.clientX, e.clientY);
    
    // 步骤3: 检查编辑状态
    // 确认是否已有文本正在编辑
    if (textService.getTexts().some((text: TextData) => text.isEditing)) {
      // 如果已有文本编辑中，则忽略本次双击事件
      // 防止多个文本同时编辑
      return;
    }
    
    // 步骤4: 检测点击位置是否在现有文本上
    const clickedTextIndex = findClickedText(point);
    if (clickedTextIndex !== -1) {
      // 步骤5.1: 处理点击在现有文本上的情况
      // 获取文本数组
      const texts = textService.getTexts();
      if (texts[clickedTextIndex]) {
        // 将点击的文本标记为编辑状态
        texts[clickedTextIndex].isEditing = true;
        
        // 步骤5.2: 清除选择状态
        // 移除任何选择框和选中状态
        selectionService.clearSelection();
        
        // 清除当前悬停的文本索引，防止显示提示框
        setHoveredTextIndex(-1);
        
        // 调用编辑方法，打开文本编辑模式，并传递点击坐标
        textService.editText(clickedTextIndex, e.clientX, e.clientY);
        
        render();
      }
      return;
    }
    
    // 步骤6: 处理在空白区域双击的情况（创建新文本）
    // 步骤6.1: 防止重复触发
    // 清除任何已存在的文本添加计时器
    if (textAddTimeoutRef.current) {
      clearTimeout(textAddTimeoutRef.current);
    }
    
    // 步骤6.2: 设置计时器延迟添加文本
    textAddTimeoutRef.current = setTimeout(() => {
      // 在双击位置添加空文本，将进入编辑状态
      textService.addText(point.x, point.y);
      // 触发重新渲染，确保文本显示在画布上
      render();
      // 重置计时器引用
      textAddTimeoutRef.current = null;
    }, 10);
    
    // 步骤6.3: 阻止事件冒泡
    // 防止事件传播到其他处理器，避免干扰文本输入
    e.stopPropagation();
  }, [canvasMode, getCanvasPoint, textService, render, findClickedText, selectionService, setHoveredTextIndex]);

  return (
    <div 
      ref={containerRef} 
      className="canvas-container"
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden'
      }}
    >
      <canvas
        ref={canvasRef}
        width={viewportService.getCanvasSize().width}
        height={viewportService.getCanvasSize().height}
        onContextMenu={handleContextMenu}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onDoubleClick={handleDoubleClick}
        onClick={handleControlPointClick}
        style={{ 
          display: 'block',
          width: '100%',
          height: '100%',
          touchAction: 'none',
          cursor: isSpacePressed || isRightDragging ? 
            (viewportService.isDraggingViewport() ? 'grabbing' : 'grab') : 'default',
          backgroundColor: 'transparent',
          opacity: 1
        }}
        tabIndex={0}
      />
      
      {/* 回到内容按钮 */}
      {isContentOutOfView && (
        <button
          className="go-to-content-button"
          onClick={handleGoToContent}
          style={{
            position: 'absolute',
            bottom: '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '6px 12px',
            backgroundColor: '#f5f5f5',
            color: '#666',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            fontWeight: 'normal',
            zIndex: 1000,
            transition: 'all 0.1s ease',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            opacity: 1,
            animation: 'fadeIn 0.1s ease'
          }}
          onMouseEnter={(e) => {
            // 使用统一的CSS变换，而不是直接修改样式
            e.currentTarget.style.backgroundColor = '#e8e8e8';
          }}
          onMouseLeave={(e) => {
            // 使用统一的CSS变换，而不是直接修改样式
            e.currentTarget.style.backgroundColor = '#f5f5f5';
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'all 0.1s ease',
          }}>
            <span style={{ transition: 'all 0.1s ease' }}>回到内容</span>
          </div>
          <style>
            {`
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
            `}
          </style>
        </button>
      )}
      
      {/* 文本提示框 */}
      {hoveredTextIndex !== -1 && !textService.getTexts()[hoveredTextIndex]?.isEditing && (
        <div
          className="text-tooltip"
          style={{
            position: 'absolute',
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '13px',
            zIndex: 1000,
            pointerEvents: 'none',
            transform: 'translateX(-50%)',
            maxWidth: '280px',
            wordBreak: 'break-word',
            textAlign: 'center',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
            backdropFilter: 'blur(2px)'
          }}
        >
          {(() => {
            const text = textService.getTexts()[hoveredTextIndex];
            
            // 只显示注释内容
            if (text?.annotation) {
              // 如果注释长度超过80个字符，截断并显示省略号
              if (text.annotation.length > 80) {
                return text.annotation.substring(0, 80) + '...';
              }
              return text.annotation;
            }
            
            // 如果没有注释，返回空字符串（不会被执行到，因为我们已经在外层条件中过滤了）
            return '';
          })()}
          <div
            style={{
              position: 'absolute',
              bottom: '-8px',
              left: '50%',
              width: '16px',
              height: '8px',
              backgroundColor: 'transparent',
              overflow: 'hidden',
              transform: 'translateX(-50%)',
              zIndex: -1
            }}
          >
            <div 
              style={{
                width: '16px',
                height: '16px',
                backgroundColor: 'rgba(0, 0, 0, 0.75)',
                transform: 'rotate(45deg) translateY(-12px)',
                borderRadius: '2px'
              }}
            />
          </div>
        </div>
      )}
      
      {/* 左下角按钮组 */}
      <div 
        className="bottom-left-buttons"
        style={{
          position: 'absolute',
          left: '20px',
          bottom: '20px',
          zIndex: 1000,
          pointerEvents: 'none', // 不捕获鼠标事件，事件穿透到画布
        }}
      >
        {/* 胶囊容器 - 包含所有按钮 */}
        <div 
          className="button-capsule"
          onMouseEnter={(e) => {
            // 获取zoom-lock-button元素
            const target = e.currentTarget;
            const zoomButton = target.querySelector('.zoom-lock-button');
            if (!zoomButton) return;
            
            // 获取zoom-lock-button的位置信息
            const zoomButtonRect = zoomButton.getBoundingClientRect();
            
            // 计算圆形按钮的中心坐标
            const centerX = zoomButtonRect.left + zoomButtonRect.width / 2;
            const centerY = zoomButtonRect.top + zoomButtonRect.height / 2;
            
            // 计算鼠标到圆心的距离
            const distance = Math.sqrt(
              Math.pow(e.clientX - centerX, 2) + 
              Math.pow(e.clientY - centerY, 2)
            );
            
            // 判断鼠标是否在缩放锁定按钮上（圆形区域内）
            // 按钮半径是宽度的一半
            const radius = zoomButtonRect.width / 2;
            const isOverZoomButton = distance <= radius;
            
            // 只有当鼠标确实在缩放锁定按钮上时才展开胶囊
            if (isOverZoomButton) {
              setShowExtendedPanel(true);
            }
          }}
          onMouseMove={(e) => {
            // 当鼠标在胶囊内移动时，如果已经展开，保持展开状态
            if (showExtendedPanel) {
              // 获取按钮容器的位置信息
              const containerRect = e.currentTarget.getBoundingClientRect();
              
              // 判断鼠标是否仍在容器内
              const isInContainer = 
                e.clientX >= containerRect.left && 
                e.clientX <= containerRect.right && 
                e.clientY >= containerRect.top && 
                e.clientY <= containerRect.bottom;
              
              // 如果鼠标在容器内，保持展开状态
              if (isInContainer) {
                setShowExtendedPanel(true);
              }
            }
          }}
          onMouseLeave={() => {
            // 移除延时，立即隐藏扩展面板
            setShowExtendedPanel(false);
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'all 0.1s ease',
            backgroundColor: (showExtendedPanel || isFixed) ? '#f5f5f5' : 'transparent',
            border: '1px solid ' + ((showExtendedPanel || isFixed) ? '#ccc' : 'transparent'),
            borderRadius: (showExtendedPanel || isFixed) ? '4px' : '50%',
            boxShadow: (showExtendedPanel || isFixed) ? '0 2px 5px rgba(0,0,0,0.1)' : 'none',
            position: 'relative',
            padding: '6px',
            overflow: 'hidden',
            pointerEvents: showExtendedPanel ? 'auto' : 'none', // 未展开时不捕获鼠标事件
          }}
        >
          {/* 主按钮 - 缩放锁定 */}
          <button
            className="zoom-lock-button"
            tabIndex={-1}
            onClick={() => {
              const newZoomLockState = !isZoomLocked;
              setIsZoomLocked(newZoomLockState);
              viewportService.setZoomDisabled(newZoomLockState);
              
              // 显示状态消息
              if (newZoomLockState) {
                showMessage("缩放已锁定");
              } else {
                showMessage("缩放已解锁");
              }
              
              // 如果关闭缩放锁定，同时关闭固定状态
              if (!newZoomLockState && isFixed) {
                setIsFixed(false);
                showMessage("画布已取消固定");
              }
            }}
            onMouseEnter={() => {
              setIsZoomButtonHovered(true);
              setShowExtendedPanel(true);
            }}
            onMouseLeave={() => setIsZoomButtonHovered(false)}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              backgroundColor: isZoomLocked ? '#e74c3c' : '#f5f5f5',
              border: 'none',
              boxShadow: isZoomLocked ? '0 2px 5px rgba(0,0,0,0.15)' : 'none',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              transition: isZoomLocked ? 'all 0.1s ease' : 'transform 0.1s ease, background-color 0.05s ease',
              position: 'relative',
              zIndex: 3,
              transform: isZoomButtonHovered && !isZoomLocked ? 'scale(1.5)' : 'scale(1)',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
              pointerEvents: 'auto', // 确保按钮始终可以捕获鼠标事件
            }}
            title={isZoomLocked ? "解锁缩放" : "锁定缩放"}
          >
            <svg 
              width="18" 
              height="18" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke={isZoomLocked ? "white" : "#555"}
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              {isZoomLocked ? (
                <>
                  {/* 锁定缩放状态 - 带叉的放大镜 */}
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  {/* 叉号 */}
                  <line x1="7" y1="7" x2="15" y2="15" strokeWidth="2" />
                  <line x1="15" y1="7" x2="7" y2="15" strokeWidth="2" />
                </>
              ) : (
                <>
                  {/* 未锁定状态 - 缩放图标 */}
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  <line x1="11" y1="8" x2="11" y2="14" />
                  <line x1="8" y1="11" x2="14" y2="11" />
                </>
              )}
            </svg>
          </button>

          {/* 固定画布按钮 - 仅在鼠标悬停或激活时显示 */}
          <button
            className="fix-button"
            tabIndex={-1}
            onClick={() => {
              const newFixedState = !isFixed;
              setIsFixed(newFixedState);
              
              // 显示状态消息
              if (newFixedState) {
                showMessage("画布已固定");
              } else {
                showMessage("画布已取消固定");
              }
              
              // 如果开启固定，同时锁定缩放
              if (newFixedState) {
                setIsZoomLocked(true);
                viewportService.setZoomDisabled(true);
              }
            }}
            onMouseEnter={() => {
              setIsFixButtonHovered(true);
              setShowExtendedPanel(true);
            }}
            onMouseLeave={() => setIsFixButtonHovered(false)}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              backgroundColor: isFixed ? '#4a90e2' : '#f5f5f5',
              border: 'none',
              boxShadow: isFixed ? '0 2px 5px rgba(0,0,0,0.15)' : 'none',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              transition: isFixed ? 'all 0.1s ease' : 'transform 0.1s ease, opacity 0.1s ease, margin-left 0.1s ease, background-color 0.05s ease',
              opacity: showExtendedPanel || isFixed ? 1 : 0,
              transform: (showExtendedPanel || isFixed) 
                ? (isFixButtonHovered && !isFixed ? 'scale(1.5)' : 'scale(1)') 
                : 'scale(0.8)',
              marginLeft: showExtendedPanel || isFixed ? '0' : '-36px',
              pointerEvents: showExtendedPanel || isFixed ? 'auto' : 'none',
              position: 'relative',
              zIndex: isZoomLocked ? 1 : 2,
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none'
            }}
            title={isFixed ? "解除固定画布" : "固定画布"}
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke={isFixed ? "white" : "#666"}
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              {/* 图钉图标 */}
              <path d="M9 4v6l-2 4v2h10v-2l-2-4V4" />
              <path d="M12 16v5" />
              <path d="M8 4h8" />
              <circle cx="12" cy="4" r="2" fill={isFixed ? "white" : "none"} />
            </svg>
          </button>
        </div>
      </div>
      
      {/* 添加重置缩放按钮 */}
      {isNearZoomLimit && (
        <button
          className="reset-zoom-button"
          onClick={handleResetZoom}
          style={{
            position: 'absolute',
            bottom: isContentOutOfView ? '30px' : '80px', // 如果内容在视图外，把重置缩放按钮下移
            left: '50%', 
            transform: 'translateX(-50%)',
            padding: '6px 12px',
            backgroundColor: '#f5f5f5',
            color: '#666',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            fontWeight: 'normal',
            zIndex: 1000,
            transition: 'all 0.1s ease',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            opacity: 1,
            animation: 'fadeIn 0.1s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#e8e8e8';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#f5f5f5';
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'all 0.1s ease',
          }}>
            <span style={{ transition: 'all 0.1s ease' }}>重置缩放</span>
          </div>
          <style>
            {`
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
            `}
          </style>
        </button>
      )}
      
      {/* 状态消息提示 */}
      {showStatusMessage && (
        <div
          className="status-message"
          style={{
            position: 'absolute',
            bottom: '75px',  // 更靠近胶囊按钮
            left: '20px',
            padding: '6px 12px',
            backgroundColor: '#f5f5f5',
            color: '#666',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
            fontSize: '14px',
            zIndex: 1000,
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            pointerEvents: 'none', // 消息不应捕获鼠标事件
          }}
        >
          <div
            key={statusMessage} // 添加key确保每次消息变化时创建新的元素
            style={{
              animation: 'fadeInOut 2s ease'
            }}
          >
            <style>
              {`
                @keyframes fadeInOut {
                  0% { opacity: 0; transform: translateY(5px); }
                  5% { opacity: 1; transform: translateY(0); }
                  95% { opacity: 1; transform: translateY(0); }
                  100% { opacity: 0; transform: translateY(-5px); }
                }
              `}
            </style>
            {statusMessage}
          </div>
        </div>
      )}
    </div>
  );
};

export default CanvasBoard;